import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Dropdown,
  Empty,
  Flex,
  Input,
  message,
  Popover,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  DeleteOutlined,
  DownOutlined,
  EditOutlined,
  FilterOutlined,
  LinkedinFilled,
  MailOutlined,
  PlusOutlined,
  SearchOutlined,
  ShareAltOutlined,
  RightOutlined,
  ThunderboltOutlined,
  UnorderedListOutlined,
  AppstoreOutlined,
  UserAddOutlined,
  UserSwitchOutlined,
} from '@ant-design/icons';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useFieldConfig } from '../hooks/useDropdownFields';
import { useMenuPermission } from '../hooks/useMenuPermission';
import { usePermissions } from '../hooks/usePermissions';
import { getCrudPermissionKey } from '../utils/actionPermission';
import { checkPermission, isActionValueAllowed } from '../utils/roleAllowsAction';
import { isActionBlocked, isRuleActionBlocked, ruleActionDisabledReason, actionDoneState, conditionMatches } from '../utils/actionRuleEngine';
import { getModuleDataList } from '../services/dropdownApi';
import { getBaseModuleName } from '../utils/modulePath';
import { mapListResponse } from '../services/responseMapping/mapListResponse';
import { clearAuthTokenOverride, fetchJsonWithAuth, setAuthTokenOverride } from '../services/authApi';
import { APP_ID, AUTH_URL } from '../services/apiConfig';
import AppBreadcrumb from './Breadcrumb';
import CustomPagination from './CustomPagination';
import DynamicTabs from './DynamicTabs';
import ViewSummaryWidget from './ViewSummaryWidget';
import DynamicFieldFilter from './filters/DynamicFieldFilter';
import eyeOutlinedIcon from './images/common/eyeoutlined.svg';
import frameIcon from './images/common/frame.svg';
import unorderedListOutlinedIcon from './images/common/unorderedlistoutlined.svg';

const { Text } = Typography;

function getFieldLabel(field) {
  return firstPresentValue(field?.label, field?.fieldLabel, field?.name, field?.fieldName, field?.value, field?.field);
}

function getFieldValue(field) {
  return firstPresentValue(field?.value, field?.field, field?.fieldName, field?.name, field?.label);
}

function firstPresentValue(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== '');
}

function normalizePathSegments(fieldKey) {
  return String(fieldKey ?? '')
    .replace(/\[(\-?\d+)\]/g, '.$1')
    .split('.')
    .filter(Boolean);
}

function getNestedRecordValue(record, fieldKey) {
  if (!fieldKey || typeof fieldKey !== 'string') return undefined;

  return normalizePathSegments(fieldKey).reduce((value, segment) => {
    if (value === null || value === undefined) return undefined;

    if (/^-?\d+$/.test(segment)) {
      if (!Array.isArray(value)) return undefined;
      const index = Number(segment);
      const resolvedIndex = index < 0 ? value.length + index : index;
      return value[resolvedIndex];
    }

    return value?.[segment];
  }, record);
}

function getRecordRawValue(record, fieldKey) {
  if (fieldKey === 'id') return record?.id ?? record?._id;

  const directValue = record?.[fieldKey];

  return directValue ?? getNestedRecordValue(record, fieldKey);
}

function getLastArrayItemValue(value, childField) {
  if (!Array.isArray(value) || value.length === 0 || !childField) return undefined;

  const lastItem = value[value.length - 1];
  if (lastItem === null || lastItem === undefined) return undefined;
  if (typeof lastItem !== 'object') return lastItem;

  return getNestedRecordValue(lastItem, childField);
}

function isExternalSourceValue(value) {
  if (Array.isArray(value)) return value.some(isExternalSourceValue);
  if (value === null || value === undefined) return false;

  const normalized = String(value).trim().toLowerCase();
  if (!normalized) return false;

  return !['all', 'internal'].includes(normalized);
}

function getExternalProfileSource(record) {
  const flatProfileKey = Object.keys(record ?? {}).find((key) => (
    /^customFields\.[^.]*profiledata\./i.test(key)
    && !/^customFields\.internalprofiledata\./i.test(key)
  ));

  if (flatProfileKey) {
    return flatProfileKey
      .replace(/^customFields\./i, '')
      .replace(/profiledata\..*$/i, '');
  }

  const customFields = record?.customFields;
  if (!customFields || typeof customFields !== 'object') return '';

  const profileKey = Object.keys(customFields).find((key) => (
    /profiledata$/i.test(key)
    && !/^internalprofiledata$/i.test(key)
    && customFields[key]
    && typeof customFields[key] === 'object'
  ));

  return profileKey ? profileKey.replace(/profiledata$/i, '') : '';
}

function isExternalSourceField(field) {
  if (typeof field?.isVisible === 'boolean' && !field.isVisible) return false;
  if (typeof field?.isVisible === 'string' && field.isVisible.toLowerCase() === 'false') return false;

  const fieldKey = getFieldValue(field);
  if (!fieldKey || typeof fieldKey !== 'string') return false;

  return (
    /^customFields\.[^.]*profiledata\./i.test(fieldKey)
    && !/^customFields\.internalprofiledata\./i.test(fieldKey)
  );
}

function isExternalSourceRecord(record) {
  const explicitSources = [
    record?.sourceType,
    record?.profileSource,
    record?.selectedSource,
    record?.customFields?.sourceType,
    record?.customFields?.profileSource,
  ];

  return explicitSources.some(isExternalSourceValue) || Boolean(getExternalProfileSource(record));
}

function getRecordValue(record, fieldKey) {
  const value = getRecordRawValue(record, fieldKey);

  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return Object.values(value).join(' ');

  return value;
}

function getConfiguredDefaultValue(field) {
  const value = field?.defaultValue;
  if (value === null || value === undefined) return '';
  return String(value).trim();
}

function shouldTreatZeroAsEmpty(field) {
  return Boolean(field?.renderConfig?.treatZeroAsEmpty);
}

function isEmptyListValue(value, field = null) {
  if (value === null || value === undefined || value === '' || value === '-') return true;
  if (shouldTreatZeroAsEmpty(field) && (value === 0 || value === '0')) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === 'object') return Object.keys(value).length === 0;
  return false;
}

function applyConfiguredDefaultValue(field, value) {
  const defaultValue = getConfiguredDefaultValue(field);
  return defaultValue && isEmptyListValue(value, field) ? defaultValue : value;
}

function formatAvatarDateTime(value) {
  if (!value) return '';

  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(date);
  const getPart = (type) => parts.find((part) => part.type === type)?.value ?? '';
  const dateText = `${getPart('month')} ${getPart('day')},${getPart('year')}`;
  const timeText = `${getPart('hour')}:${getPart('minute')} ${getPart('dayPeriod')}`;

  return `${dateText} | ${timeText}`;
}

function getAvatarInitial(value) {
  return String(value || 'U').trim().charAt(0).toUpperCase() || 'U';
}

function getAvatarFieldValue(item, keys) {
  const key = keys.find((candidateKey) => {
    const value = getRecordRawValue(item, candidateKey);
    return value !== null && value !== undefined && value !== '';
  });

  return key ? getRecordValue(item, key) : '';
}

function normalizeAvatarItems(field, record) {
  const rawValue = getRecordRawValue(record, field.key);
  const items = Array.isArray(rawValue) ? rawValue : (rawValue ? [rawValue] : []);
  const config = field.renderConfig ?? {};
  const nameKeys = [
    config.nameField,
    config.labelField,
    'recruiterName',
    'name',
    'fullName',
    'displayName',
    'userName',
    'email',
    'recruiterEmail',
  ].filter(Boolean);
  const metaKeys = [
    config.metaField,
    config.dateField,
    config.assignedDateField,
    'assignedDateTime',
    'assignedDate',
    'createdAt',
  ].filter(Boolean);

  return items.map((item, index) => {
    const recordItem = typeof item === 'object' && item !== null ? item : { value: item };
    const name = getAvatarFieldValue(recordItem, nameKeys) || String(item || 'User');
    const metaValue = getAvatarFieldValue(recordItem, metaKeys);

    return {
      key: getAvatarFieldValue(recordItem, ['recruiterId', 'id', '_id', 'email', 'recruiterEmail']) || `${name}-${index}`,
      name,
      initial: getAvatarInitial(name),
      meta: formatAvatarDateTime(metaValue),
    };
  });
}

function AvatarTooltipContent({ item }) {
  return (
    <div className="job-avatar-tooltip">
      <div className="job-avatar-tooltip-name">{item.name}</div>
      {item.meta && <div className="job-avatar-tooltip-meta">{item.meta}</div>}
    </div>
  );
}

function AvatarMoreTooltipContent({ items }) {
  return (
    <div className="job-avatar-more-tooltip">
      {items.map((item) => (
        <AvatarTooltipContent item={item} key={item.key} />
      ))}
    </div>
  );
}

function isTrueValue(value) {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') return value.toLowerCase() === 'true';

  return Boolean(value);
}

function stringifyLinkPart(value) {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.join(',');
  if (typeof value === 'object') return Object.values(value).join(' ');

  return String(value);
}

function fillLinkTemplate(template, record) {
  if (!template) return '';

  return String(template)
    .replace(/\{([^}]+)\}/g, (_, key) => encodeURIComponent(stringifyLinkPart(getRecordRawValue(record, key))))
    .replace(/:([A-Za-z_][\w.]*)/g, (_, key) => encodeURIComponent(stringifyLinkPart(getRecordRawValue(record, key))));
}

function isUrlLike(value) {
  return /^(https?:\/\/|\/)/i.test(String(value));
}

function getLinkTarget(target, href) {
  const normalizedTarget = String(target ?? '').trim().toLowerCase();

  if (normalizedTarget === 'blank' || normalizedTarget === '_blank') return '_blank';
  if (normalizedTarget === 'self' || normalizedTarget === '_self') return '_self';
  if (normalizedTarget) return target;
  if (/^https?:\/\//i.test(String(href))) return '_blank';

  return undefined;
}

function isExternalUrl(value) {
  return /^https?:\/\//i.test(String(value));
}

function getFieldHref(field, record, value) {
  const template = field.routeUrl
    ?? field.routeURL
    ?? field.linkTemplate
    ?? field.linkUrl
    ?? field.href
    ?? field.url
    ?? field.action;

  if (template) return fillLinkTemplate(template, record);
  if (isUrlLike(value)) return String(value);

  return '';
}

function highlightText(text, query) {
  if (!query || query.length < 3 || !text || typeof text !== 'string' || text === '-') return text;
  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts = [];
  let lastIndex = 0;

  while (lastIndex <= text.length) {
    const matchIndex = lowerText.indexOf(lowerQuery, lastIndex);
    if (matchIndex === -1) {
      parts.push(text.slice(lastIndex));
      break;
    }
    if (matchIndex > lastIndex) parts.push(text.slice(lastIndex, matchIndex));
    parts.push(
      <mark
        key={matchIndex}
        className="search-highlight"
        style={{ backgroundColor: '#ffff00', color: 'inherit', padding: 0, borderRadius: 2 }}
      >
        {text.slice(matchIndex, matchIndex + query.length)}
      </mark>
    );
    lastIndex = matchIndex + query.length;
  }

  return parts;
}

function renderLinkedValue(field, record, value, searchQuery = '') {
  const href = getFieldHref(field, record, value);
  const target = getLinkTarget(field.linkTarget, href);
  const highlightedValue = highlightText(value, searchQuery);

  if (isExternalUrl(href)) {
    return (
      <Tooltip title={value !== '-' ? value : undefined}>
        <a
          className="job-cell-link job-cell-value"
          href={href}
          rel={target === '_blank' ? 'noreferrer' : undefined}
          target={target}
        >
          {highlightedValue}
        </a>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={value !== '-' ? value : undefined}>
      <RouterLink
        className="job-cell-link job-cell-value"
        target={target}
        to={href || '#'}
      >
        {highlightedValue}
      </RouterLink>
    </Tooltip>
  );
}

function getDateDisplayMode(field) {
  const candidates = [
    field?.props,
    field?.props?.format,
    field?.props?.display,
    field?.props?.dateDisplay,
    field?.renderConfig?.format,
    field?.renderConfig?.display,
    field?.renderConfig?.dateDisplay,
    field?.dateDisplay,
  ];

  const normalized = candidates
    .map((value) => (typeof value === 'string' ? value.trim().toLowerCase() : ''))
    .find(Boolean);

  if (normalized === 'datetime' || normalized === 'time') return normalized;
  return 'date';
}

function formatDateValue(value, field) {
  if (value === null || value === undefined || value === '') return '-';

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) return getRecordValue({ value }, 'value');

  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
    timeZone: 'UTC',
  }).formatToParts(date);

  const getPart = (type) => parts.find((part) => part.type === type)?.value ?? '';
  const dateText = `${getPart('month')}, ${getPart('day')}, ${getPart('year')}`;
  const timeText = `${getPart('hour')}:${getPart('minute')} ${getPart('dayPeriod')}`;
  const mode = getDateDisplayMode(field);

  if (mode === 'time') return timeText;
  if (mode === 'datetime') return `${dateText} | ${timeText}`;
  return dateText;
}
function isDateField(field) {
  return /date/i.test(`${field.key ?? ''} ${field.label ?? ''} ${field.type ?? ''}`);
}

function normalizeTemplateValue(value) {
  if (value === null || value === undefined || value === '') return '-';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return Object.values(value).join(' ');
  return value;
}

function getDisplayTemplateValue(record, placeholderKey, field) {
  const key = String(placeholderKey ?? '').trim();
  if (!key) return '-';

  const normalizedKey = key.replace(/^record\./, '');
  const arrayChildField = field?.renderConfig?.lastItemField ?? field?.renderConfig?.arrayDisplayField;

  if (arrayChildField) {
    // The configured ListView field may be a computed/virtual key such as
    // `_fmt_client_rate`. In that case the actual array path is available only
    // in the display template, for example `jobPayDetails.clientBudgetStart`.
    // Resolve the placeholder's array root directly from the record and use
    // the same final array object for every placeholder in this field.
    const pathSegments = normalizePathSegments(normalizedKey);

    for (let splitIndex = pathSegments.length - 1; splitIndex >= 1; splitIndex -= 1) {
      const parentPath = pathSegments.slice(0, splitIndex).join('.');
      const parentValue = getRecordRawValue(record, parentPath);

      if (!Array.isArray(parentValue)) continue;

      const lastItem = parentValue[parentValue.length - 1];
      if (lastItem === null || lastItem === undefined) return '-';

      const childPath = pathSegments.slice(splitIndex).join('.');
      return normalizeTemplateValue(getNestedRecordValue(lastItem, childPath));
    }

    // Preserve support for configurations where the field itself points to
    // the array instead of using a virtual/computed field key.
    const fieldKey = field?.key;
    const rawFieldValue = fieldKey ? getRecordRawValue(record, fieldKey) : undefined;

    if (fieldKey && Array.isArray(rawFieldValue)) {
      const lastItem = rawFieldValue[rawFieldValue.length - 1];
      if (normalizedKey === fieldKey) return normalizeTemplateValue(lastItem);

      const fieldPrefix = `${fieldKey}.`;
      if (normalizedKey.startsWith(fieldPrefix)) {
        return normalizeTemplateValue(
          getNestedRecordValue(lastItem, normalizedKey.slice(fieldPrefix.length)),
        );
      }
    }
  }

  return getRecordValue(record, normalizedKey);
}

function cleanGroupedTemplateSeparators(value) {
  return String(value ?? '')
    .replace(/\s+/g, ' ')
    // Drop separators left at the beginning/end when one grouped value is empty.
    .replace(/^\s*[-–—/:|·,]+\s*/, '')
    .replace(/\s*[-–—/:|·,]+\s*$/, '')
    // Remove duplicated/orphan separators created by adjacent empty placeholders.
    .replace(/\s*([-–—/:|·,])\s*([-–—/:|·,])\s*/g, ' $2 ')
    // "3 - Yrs" / "$ - Hourly" -> "3 Yrs" / "$ Hourly" when the right token vanished.
    .replace(/\s+[-–—]\s+(?=[A-Za-z$€£₹])/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function formatDisplayTemplate(template, record, field) {
  if (typeof template !== 'string' || template.trim() === '') return '';

  let hasResolvedValue = false;
  const rendered = template.replace(/\{\{\s*([^}]+?)\s*\}\}/g, (_, key) => {
    const value = getDisplayTemplateValue(record, key, field);
    if (isEmptyListValue(value, field)) return '';
    hasResolvedValue = true;
    return String(value);
  });

  // If every placeholder is empty, static template text (e.g. "Yrs" or "/ Hourly")
  // must not prevent the configured Default Value from being used.
  if (!hasResolvedValue) return '';

  const normalized = rendered.replace(/\s+/g, ' ').trim();
  return field?.renderConfig?.cleanEmptyTemplateSeparators
    ? cleanGroupedTemplateSeparators(normalized)
    : normalized;
}

function formatFieldValue(field, record) {
  const rawValue = getRecordRawValue(record, field.key);
  const templatedValue = formatDisplayTemplate(field.displayTemplate, record, field);
  const arrayChildField = field.renderConfig?.lastItemField ?? field.renderConfig?.arrayDisplayField;

  if (templatedValue) return templatedValue;

  if (arrayChildField) {
    const lastArrayValue = getLastArrayItemValue(rawValue, arrayChildField);
    if (!isEmptyListValue(lastArrayValue, field)) {
      if (Array.isArray(lastArrayValue)) return applyConfiguredDefaultValue(field, lastArrayValue.join(', '));
      if (typeof lastArrayValue === 'object') return applyConfiguredDefaultValue(field, Object.values(lastArrayValue).join(' '));
      return lastArrayValue;
    }
    return applyConfiguredDefaultValue(field, '-');
  }

  if (isDateField(field)) return applyConfiguredDefaultValue(field, formatDateValue(rawValue, field));

  return applyConfiguredDefaultValue(field, getRecordValue(record, field.key));
}

function isConfiguredDefaultValueActive(field, record) {
  if (!getConfiguredDefaultValue(field)) return false;

  const templatedValue = formatDisplayTemplate(field.displayTemplate, record, field);
  if (templatedValue) return false;

  const rawValue = getRecordRawValue(record, field.key);
  const arrayChildField = field.renderConfig?.lastItemField ?? field.renderConfig?.arrayDisplayField;

  if (arrayChildField) {
    return isEmptyListValue(getLastArrayItemValue(rawValue, arrayChildField), field);
  }

  if (isDateField(field)) return isEmptyListValue(formatDateValue(rawValue, field), field);

  return isEmptyListValue(getRecordValue(record, field.key), field);
}

function capitalizeWords(value) {
  if (value === '-' || typeof value !== 'string' || !value.trim()) return value;

  return value.replace(/\b([A-Za-z])([A-Za-z]*)\b/g, (word, first, rest) => {
    if (word.length > 1 && word === word.toUpperCase()) return word;
    return `${first.toUpperCase()}${rest.toLowerCase()}`;
  });
}

function formatExperienceRange(value) {
  if (typeof value !== 'string' || !value.includes('-')) return value;

  return value
    .replace(/(^|\s)-\s*(\d+)(?=\s*[A-Za-z])/, '$10-$2')
    .replace(/(\d+)\s*-\s*(?=[A-Za-z])/, '$1-0 ');
}

function getOrderedFields(fields) {
  const indexedFields = fields.map((field, index) => ({ field, index }));
  const hasOrder = indexedFields.some(({ field }) => field.order !== undefined && field.order !== null && field.order !== '');

  if (!hasOrder) return fields;

  return [...indexedFields]
    .sort((a, b) => {
      const firstOrder = Number(a.field.order);
      const secondOrder = Number(b.field.order);
      const firstHasOrder = Number.isFinite(firstOrder);
      const secondHasOrder = Number.isFinite(secondOrder);

      if (firstHasOrder && secondHasOrder && firstOrder !== secondOrder) return firstOrder - secondOrder;
      if (firstHasOrder !== secondHasOrder) return firstHasOrder ? -1 : 1;

      return a.index - b.index;
    })
    .map(({ field }) => field);
}

function getActionIconUrl(action) {
  return action.iconUrl ?? action.iconURL ?? action.icon ?? action.iconPath;
}

function normalizeActionButtons(actionButtons) {
  if (!Array.isArray(actionButtons)) return [];

  return actionButtons
    .map((action, index) => ({
      ...action,
      key: action.key ?? action.name ?? action.label ?? action.routeUrl ?? action.type ?? String(index),
      label: action.label ?? action.name ?? action.title ?? action.type ?? 'Action',
      iconUrl: getActionIconUrl(action),
    }))
    .filter((action) => action.iconUrl);
}

// isActionButtonVisible — a column (in-cell) action button now shares the SAME
// rule system as the row-dropdown and toolbar actions, so an action configured
// once is gated identically wherever it renders:
//   1. Module Action Rules (Admin → Action Rules) keyed by the action's `key` —
//      isRuleActionBlocked hides it (e.g. "Not my submission — hide Resubmit",
//      createdBy ne {currentUserId}); the same {currentUserId} token and
//      includes/notIncludes/ne operators the other surfaces use.
//   2. An inline `conditions` array on the button itself (same {field, operator,
//      value} shape) — "show only if EVERY condition matches", so an admin can
//      gate a single button without authoring a module rule.
//   3. The legacy showIfField/showIfValue single-field equality (kept for
//      backward compatibility; expressed through the same evaluator).
function isActionButtonVisible(action, record, actionRules = [], scope) {
  if (isRuleActionBlocked(action.key, record, actionRules, scope)) return false;

  if (Array.isArray(action.conditions) && action.conditions.length > 0) {
    if (!action.conditions.every((condition) => conditionMatches(condition, record, scope))) return false;
  }

  const fieldKey = action?.showIfField ?? action?.visibleWhenField;
  if (!fieldKey) return true;
  const operator = action?.showIfOperator || 'eq';
  const expected = action?.showIfValue ?? action?.visibleWhenValue ?? '';
  return conditionMatches({ field: fieldKey, operator, value: expected }, record, scope);
}

// Gates admin-configured column action buttons (e.g. the inline "Edit" pencil
// icon on a list field) by the same add/edit/delete menuPermission flags used
// for the dropdown row actions (see getCrudPermissionKey usage above), so an
// icon configured in Admin can't bypass a role's edit/add/delete restriction.
function isActionButtonAllowed(action, permissions = {}) {
  const pk = getCrudPermissionKey(action);
  if (pk === 'add') return permissions.canAdd !== false;
  if (pk === 'edit') return permissions.canEdit !== false;
  if (pk === 'delete') return permissions.canDelete !== false;
  return true;
}

function renderActionButton(action, record, onPopupAction) {
  const routeUrl = fillLinkTemplate(
    action.routeUrl ?? action.routeURL ?? action.route ?? action.url ?? action.href,
    record,
  );
  const normalizedType = normalizeComparableText(action.type ?? action.actionType);
  const label = typeof action.label === 'function' ? action.label(record) : action.label;
  const content = (
    <img
      src={action.iconUrl}
      alt={label}
      className="job-field-action-icon"
    />
  );
  const className = "job-field-action-button";

  const done = action.__done;
  const disabledReason = action.__disabledReason;
  if (done || disabledReason) {
    return (
      <Tooltip key={action.key} title={done ? done.label : disabledReason}>
        <button
          aria-label={done ? done.label : `${label} (unavailable)`}
          className={`${className} job-field-action-button--done`}
          type="button"
          disabled
        >
          {content}
        </button>
      </Tooltip>
    );
  }

  if (typeof action.onClick === 'function') {
    return (
      <Tooltip key={action.key} title={label}>
        <button
          aria-label={label}
          className={className}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            action.onClick(record);
          }}
        >
          {content}
        </button>
      </Tooltip>
    );
  }

  if ((normalizedType === 'popup' || normalizedType === 'modal' || action.popupKey) && routeUrl) {
    return (
      <Tooltip key={action.key} title={label}>
        <button
          aria-label={label}
          className={className}
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            onPopupAction?.(routeUrl, record, action);
          }}
        >
          {content}
        </button>
      </Tooltip>
    );
  }

  if (routeUrl && (normalizedType === 'page' || routeUrl.startsWith('/'))) {
    return (
      <Tooltip key={action.key} title={label}>
        <RouterLink
          aria-label={label}
          className={className}
          to={routeUrl}
        >
          {content}
        </RouterLink>
      </Tooltip>
    );
  }

  if (routeUrl) {
    return (
      <Tooltip key={action.key} title={label}>
      <a
        aria-label={label}
        className={className}
        href={routeUrl}
        rel="noreferrer"
        target="_blank"
        >
        {content}
      </a>
      </Tooltip >
    );
  }

  return (
    <Tooltip key={action.key} title={label}>
      <button
        aria-label={label}
        className={className}
        type="button"
      >
        {content}
      </button>
    </Tooltip>
  );
}



function getSecondaryFieldKeys(field) {
  const secondaryFields = Array.isArray(field.secondaryFields)
    ? field.secondaryFields
    : [];
  const secondaryField = field.secondaryField ? [field.secondaryField] : [];

  return [...secondaryFields, ...secondaryField]
    .filter((key, index, keys) => key && keys.indexOf(key) === index);
}

function getSecondaryFieldValue(field, record) {
  const separator = field.secondarySeparator ?? ' ';
  const values = getSecondaryFieldKeys(field)
    .map((key) => getRecordValue(record, key))
    .filter((value) => value !== '-');

  return values.length ? values.join(` ${separator} `) : '';
}

function formatListText(value) {
  return capitalizeWords(formatExperienceRange(value));
}

function getBadgeValueStyle(field, value) {
  if (!Array.isArray(field.valueStyles)) return null;

  const normalizedValue = normalizeComparableText(value);

  return field.valueStyles.find((style) => (
    normalizeComparableText(style?.value) === normalizedValue
    || normalizeComparableText(style?.label) === normalizedValue
  )) ?? null;
}

function getBadgeStyle(styleConfig) {
  const color = styleConfig?.color;
  const bg = styleConfig?.bg ?? styleConfig?.background ?? styleConfig?.backgroundColor;

  return {
    ...(color ? { '--job-cell-badge-color': color } : {}),
    ...(bg ? { '--job-cell-badge-bg': bg } : {}),
  };
}

function getBadgeVariant(styleConfig) {
  return normalizeComparableText(styleConfig?.varient ?? styleConfig?.variant)
    .replace(/[^a-z0-9-]/g, '-');
}

function renderBadgeField(field, value) {
  const valueStyle = getBadgeValueStyle(field, value);
  const styleConfig = valueStyle ?? field.commonStyle ?? {};
  const rawLabel = valueStyle?.label ?? value;
  const fullLabel = (typeof rawLabel === 'string' ? rawLabel.trim() : rawLabel) || '-';
  const variant = getBadgeVariant(styleConfig);
  const className = [
    'job-cell-badge',
    'job-cell-value',
    variant ? `job-cell-badge--${variant}` : '',
  ].filter(Boolean).join(' ');

  const MAX_VISIBLE_ITEMS = 1;
  const items = !valueStyle && typeof fullLabel === 'string' && fullLabel.includes(',')
    ? fullLabel.split(',').map((item) => item.trim()).filter(Boolean)
    : null;
  const displayLabel = items && items.length > MAX_VISIBLE_ITEMS
    ? `${items.slice(0, MAX_VISIBLE_ITEMS).join(', ')} +${items.length - MAX_VISIBLE_ITEMS}`
    : fullLabel;

  const badgeStyle = getBadgeStyle(styleConfig);

  return (
    <Tooltip title={fullLabel !== '-' ? fullLabel : undefined}>
      <Tag className={className} style={badgeStyle}>
        {displayLabel}
      </Tag>
    </Tooltip>
  );
}

function renderAvatarField(field, record) {
  const avatars = normalizeAvatarItems(field, record);
  const visibleAvatars = avatars.slice(0, 2);
  const overflowAvatars = avatars.slice(2);

  if (!avatars.length) {
    const fallback = getConfiguredDefaultValue(field) || '-';
    return <Text className="job-cell-primary job-cell-value">{fallback}</Text>;
  }

  return (
    <span className="job-avatar-group">
      {visibleAvatars.map((item, index) => (
        <Tooltip
          key={item.key}
          overlayClassName="job-avatar-tooltip-overlay"
          title={<AvatarTooltipContent item={item} />}
        >
          <Avatar className={`job-avatar job-avatar--${index + 1}`}>
            {item.initial}
          </Avatar>
        </Tooltip>
      ))}
      {overflowAvatars.length > 0 && (
        <Tooltip
          overlayClassName="job-avatar-tooltip-overlay job-avatar-more-tooltip-overlay"
          title={<AvatarMoreTooltipContent items={overflowAvatars} />}
        >
          <Avatar className="job-avatar job-avatar--more">
            +{overflowAvatars.length}
          </Avatar>
        </Tooltip>
      )}
    </span>
  );
}

function renderFieldValue(field, record, searchQuery = '', onPopupAction, permissions = {}, actionRules = [], scope) {
  if (normalizeComparableText(field.renderType) === 'avatar') return renderAvatarField(field, record);

  const usingDefaultValue = isConfiguredDefaultValueActive(field, record);
  const value = formatListText(formatFieldValue(field, record));
  const actionButtons = normalizeActionButtons(field.actionButtons)
    .filter((action) => isActionButtonVisible(action, record, actionRules, scope))
    .filter((action) => isActionButtonAllowed(action, permissions))
    // Attach the same done-state / rule-disabled state the row + toolbar
    // actions get, so a column button also greys out (with its doneLabel or the
    // rule's reason) instead of staying clickable.
    .map((action) => ({
      ...action,
      __done: actionDoneState(action, record, scope),
      __disabledReason: ruleActionDisabledReason(action.key, record, actionRules, scope),
    }));
  const secondaryValue = formatListText(getSecondaryFieldValue(field, record));
  const isBadge = normalizeComparableText(field.renderType) === 'badge';
  const renderedValue = isBadge && value !== '-'
    ? renderBadgeField(field, value)
    : (!isTrueValue(field.isLink) || value === '-' || usingDefaultValue
      ? <Tooltip title={value !== '-' ? value : undefined}><Text className="job-cell-primary job-cell-value">{highlightText(value, searchQuery)}</Text></Tooltip>
      : renderLinkedValue(field, record, value, searchQuery));

  return (
    <span className="job-cell-content">
      <span className="job-cell-main-line">
        {renderedValue}
        {actionButtons.length > 0 && (
          <span className="job-field-actions">
            {actionButtons
              .filter((action) => typeof action.hiddenIf !== 'function' || !action.hiddenIf(record))
              .map((action) => renderActionButton(action, record, onPopupAction))}
          </span>
        )}
      </span>
      {secondaryValue && (
        <Tooltip title={secondaryValue}>
          <span className="job-cell-secondary">
            {highlightText(secondaryValue, searchQuery)}
          </span>
        </Tooltip>
      )}
    </span>
  );
}

// Content-based column sizing — mirrors how the cell actually renders
// (primary value via formatFieldValue + secondary line + header label with its
// sort trigger) and estimates a pixel width from the longest of those, so each
// column hugs its values like the reference design instead of splitting the
// table width evenly. Values longer than MAX_COLUMN_WIDTH still ellipsize with
// a tooltip (handled by the .job-cell-* CSS).
const MIN_COLUMN_WIDTH = 130;
const MAX_COLUMN_WIDTH = 230;
const CELL_CHAR_PX = 7.2;
const CELL_PADDING_PX = 26;
const HEADER_CHAR_PX = 8;
const HEADER_SORTER_PX = 44;
const VERTICAL_SCROLLBAR_GUTTER_PX = 20;

function estimateColumnWidth(field, records) {
  const headerWidth = String(field.label ?? '').length * HEADER_CHAR_PX + HEADER_SORTER_PX;

  if (normalizeComparableText(field.renderType) === 'avatar') {
    return Math.max(Math.round(headerWidth), 120);
  }

  let maxChars = 0;
  for (const record of records) {
    const primary = String(formatListText(formatFieldValue(field, record)) ?? '');
    const secondary = String(formatListText(getSecondaryFieldValue(field, record)) ?? '');
    maxChars = Math.max(maxChars, primary.length, secondary.length);
  }

  // Badges add horizontal padding around the value.
  const extra = normalizeComparableText(field.renderType) === 'badge' ? 24 : 0;
  const contentWidth = maxChars * CELL_CHAR_PX + CELL_PADDING_PX + extra;

  return Math.round(Math.min(Math.max(headerWidth, contentWidth, MIN_COLUMN_WIDTH), MAX_COLUMN_WIDTH));
}

function getComparableValue(record, fieldKey) {
  const value = getRecordValue(record, fieldKey);
  return value === '-' ? '' : String(value);
}


function normalizeComparableText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function getTabKey(tab, index) {
  return tab?.key ?? tab?.value ?? tab?.id ?? (index !== undefined ? String(index) : undefined);
}

function normalizeTabKey(tabKey) {
  return normalizeComparableText(tabKey);
}

function isAllTabKey(tabKey) {
  return ['all', ''].includes(normalizeTabKey(tabKey));
}

function recordMatchesTab(record, activeTab, activeTabConfig, fields, tabField) {
  if (isAllTabKey(activeTab)) return true;

  const tabValue = activeTabConfig?.filterValue
    ?? activeTabConfig?.status
    ?? activeTabConfig?.title
    ?? activeTabConfig?.tabTitle
    ?? activeTabConfig?.name
    ?? (typeof activeTabConfig?.label === 'string' ? activeTabConfig.label : undefined)
    ?? activeTabConfig?.value
    ?? activeTabConfig?.key
    ?? activeTab;
  const normalizedTabValue = normalizeComparableText(tabValue);
  const explicitField = activeTabConfig?.field
    ?? activeTabConfig?.fieldName
    ?? activeTabConfig?.filterField
    ?? activeTabConfig?.dataIndex
    ?? tabField;

  if (explicitField) {
    return normalizeComparableText(getRecordValue(record, explicitField)) === normalizedTabValue;
  }

  const statusFields = fields.filter((field) => {
    const key = normalizeComparableText(field.key);
    const label = normalizeComparableText(field.label);
    return key.includes('status') || label.includes('status');
  });
  const candidateFields = statusFields.length ? statusFields : fields;

  return candidateFields.some((field) => (
    normalizeComparableText(getRecordValue(record, field.key)) === normalizedTabValue
  ));
}

function getRowKey(record, index, rowKey) {
  if (typeof rowKey === 'function') return rowKey(record, index);
  if (typeof rowKey === 'string') return record?.[rowKey];

  return record.id ?? record._id ?? record.key ?? index;
}

function isInteractiveClickTarget(target) {
  return Boolean(target?.closest?.(
    'a,button,input,textarea,select,label,[role="button"],[role="menuitem"],.ant-checkbox,.ant-radio,.ant-dropdown,.ant-dropdown-trigger,.ant-table-selection-column,.job-field-actions,.job-row-eye-button'
  ));
}

function getActionLabel(action) {
  return action.label ?? action.name ?? action.title ?? action.value;
}

function getActionKey(action, index) {
  return action.key ?? action.value ?? action.route ?? action.name ?? String(index);
}

const ACTION_ICON_MAP = {
  delete: <DeleteOutlined />,
  edit: <EditOutlined />,
  mail: <MailOutlined />,
  shareAlt: <ShareAltOutlined />,
  swap: <RightOutlined />,
  thunderbolt: <ThunderboltOutlined />,
  userAdd: <UserAddOutlined />,
  userSwitch: <UserSwitchOutlined />,
};

// antd's Dropdown `menu.items` only understands {key, label, icon, danger, ...}.
// Admin-configured actions carry extra config fields (actionType, routeTemplate,
// popupKey, popupState, ...) that must NOT be spread onto the menu item, or antd
// forwards them straight onto the underlying DOM node. An action with
// admin-configured children (actionType "group", e.g. "Shortlist") renders as
// a submenu — antd's Menu natively supports this via item.children.
function toMenuItem(action, done) {
  const item = {
    key: action.key,
    // Config-driven "done" terminus (doneWhen matched, e.g. onboardingStatus
    // set): the item shows its doneLabel ("Moved to Onboarded") and is disabled.
    label: done ? done.label : action.label,
    icon: ACTION_ICON_MAP[action.icon] ?? undefined,
    danger: done ? undefined : (action.danger || undefined),
    disabled: !!done || undefined,
    title: done ? done.label : undefined,
  };
  if (action.children?.length) {
    item.children = action.children.map((child) => toMenuItem(child));
  }
  return item;
}

// bulkDoneState — done-state for a toolbar action across the CURRENT selection:
// the action is "done" only when EVERY selected record already satisfies its
// doneWhen (you can't Move-to-Onboarding a set that's already all onboarded).
// The common case is a single selected row. Returns { label, tone } or null.
function bulkDoneState(action, records, scope) {
  if (!action?.doneWhen?.field || !records?.length) return null;
  return records.every((record) => actionDoneState(action, record, scope))
    ? actionDoneState(action, records[0], scope)
    : null;
}

// Applies predicate to every action AND recursively to its children, keeping
// a parent (e.g. a "group" action) whenever the parent itself passes,
// independent of how many children pass — so admin-configured visibility,
// permission, and blocking rules apply the same way at every nesting level.
function filterActionTree(actions, predicate) {
  return actions
    .filter(predicate)
    .map((action) => (
      action.children?.length
        ? { ...action, children: filterActionTree(action.children, predicate) }
        : action
    ));
}

// Flattens a (possibly nested) action tree depth-first, so lookups by key
// (e.g. dispatching a click on a nested "Tag To Job" action) work regardless
// of nesting.
function flattenActions(actions) {
  const flat = [];
  for (const action of actions) {
    flat.push(action);
    if (action.children?.length) flat.push(...flattenActions(action.children));
  }
  return flat;
}

function normalizeActions(actions) {
  if (!Array.isArray(actions)) return [];

  return actions
    .map((action, index) => {
      const label = getActionLabel(action);
      const key = getActionKey(action, index);

      if (!label || !key) return null;

      return {
        ...action,
        key: String(key),
        label,
        route: action.route || action.routeTemplate || (
          action.actionType === 'modal' && (action.popupKey || key)
            ? `/popup/${action.popupKey || key}`
            : ''
        ),
        children: action.children?.length ? normalizeActions(action.children) : undefined,
      };
    })
    .filter(Boolean);
}

function isChangeStatusAction(action) {
  const key = String(action?.key ?? '').trim().toLowerCase();
  const label = String(action?.label ?? '').trim().toLowerCase();
  const popupKey = String(action?.popupKey ?? '').trim().toLowerCase();
  const route = String(action?.route ?? action?.routeTemplate ?? '').trim().toLowerCase();

  return key === 'change-status'
    || key === 'changestatus'
    || label === 'change status'
    || popupKey === 'change-status'
    || route.includes('/popup/change-status');
}

function getClientSubmissionStatusIdentity(record) {
  const nested = record?.clientSubmissionStatus;
  const nestedObject = nested && typeof nested === 'object' ? nested : null;

  const id = Number(
    nestedObject?.clientSubmissionStatusId
      ?? nestedObject?.id
      ?? nestedObject?.uniqueId
      ?? record?.clientSubmissionStatusId
      ?? record?.clientStatusId
      ?? record?.['clientSubmissionStatus.clientSubmissionStatusId']
      ?? 0,
  ) || 0;

  const text = String(
    nestedObject?.clientSubmissionStatusText
      ?? nestedObject?.name
      ?? (typeof nested === 'string' ? nested : undefined)
      ?? record?.clientSubmissionStatusText
      ?? record?.clientStatusText
      ?? record?.clientStatus
      ?? record?.['clientSubmissionStatus.clientSubmissionStatusText']
      ?? '',
  ).trim();

  return { id, text: normalizeComparableText(text) };
}

function recordHasTerminalClientStatus(record, terminalStatusIds, terminalStatusNames) {
  const current = getClientSubmissionStatusIdentity(record);
  if (current.id > 0 && terminalStatusIds.has(current.id)) return true;
  return Boolean(current.text && terminalStatusNames.has(current.text));
}

function selectedRecordsHaveTerminalClientStatus(records, terminalStatusIds, terminalStatusNames) {
  if (!records?.length) return false;
  return records.some((record) => recordHasTerminalClientStatus(
    record,
    terminalStatusIds,
    terminalStatusNames,
  ));
}

function selectedRecordsAllowAction(action, records) {
  if (!action.permissionKey || !records.length) return true;
  return records.every((record) => isActionValueAllowed(
    getNestedRecordValue(record, `actionPermission.${action.permissionKey}`),
  ));
}

// isActionBlocked / isRuleActionBlocked (per-row) now live in
// ../utils/actionRuleEngine, shared with DetailHeaderCard.jsx so the two
// views can no longer drift apart on operator behavior.

// getRowActions — the per-row counterpart of `selectedActionItems` (which only
// ever fed the bulk multi-select toolbar). Applied to a SINGLE record, so a
// configured Module Action Rule can actually hide a row's own action button,
// not just an entry in the bulk dropdown that only appears after selecting rows.
function getRowActions(record, visibleActionItems, actionRules, scope) {
  return filterActionTree(visibleActionItems, (action) => (
    selectedRecordsAllowAction(action, [record])
    && !isRuleActionBlocked(action.key, record, actionRules, scope)
  ));
}

function getNextSortDirection(currentSortDir, sameField) {
  if (!sameField) return 'asc';
  if (currentSortDir === 'asc') return 'desc';
  if (currentSortDir === 'desc') return '';

  return 'asc';
}

function getSortTooltipText(sortDir) {
  if (sortDir === 'asc') return 'Ascending';
  if (sortDir === 'desc') return 'Descending';

  return 'Neutral';
}

function renderSortHeader({ label, sortDir, sortable, onSort }) {
  if (!sortable) {
    return <span className="job-table-header-label">{label}</span>;
  }

  const tooltipText = getSortTooltipText(sortDir);

  return (
    <div className="job-table-header-content">
      <span className="job-table-header-label" title={typeof label === 'string' ? label : undefined}>
        {label}
      </span>
      <Tooltip title={tooltipText} mouseEnterDelay={0.08} overlayClassName="job-sort-tooltip">
        <button
          type="button"
          className={`job-table-sort-trigger${sortDir ? ' job-table-sort-trigger--active' : ''}`}
          aria-label={`Sort ${typeof label === 'string' ? label : 'column'}, ${tooltipText}`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onSort?.();
          }}
        >
          <span className="job-table-sort-icon-stack">
            <CaretUpOutlined
              className={`job-table-sort-icon job-table-sort-icon--up${sortDir === 'asc' ? ' job-table-sort-icon--active' : ''}`}
            />
            <CaretDownOutlined
              className={`job-table-sort-icon job-table-sort-icon--down${sortDir === 'desc' ? ' job-table-sort-icon--active' : ''}`}
            />
          </span>
        </button>
      </Tooltip>
    </div>
  );
}

function getDefaultListSnapshotKey({ moduleName, activeTabScope, pageSize, sort, sortDir, queryParams }) {
  return [
    moduleName ?? '',
    activeTabScope ?? '',
    pageSize,
    sort ?? '',
    sortDir ?? '',
    queryParams ? JSON.stringify(queryParams) : '',
  ].join('|');
}

function getPositivePage(value) {
  const page = Number(value);

  return Number.isInteger(page) && page > 0 ? page : 1;
}

function getPageFromSearchParams(searchParams) {
  for (const key of searchParams.keys()) {
    const match = /^p(\d+)$/.exec(key);

    if (match) return getPositivePage(match[1]);
  }

  return 1;
}

function removePageSearchParams(searchParams) {
  [...searchParams.keys()].forEach((key) => {
    if (/^p\d+$/.test(key) || key === 'page') searchParams.delete(key);
  });
}

const EMPTY_FIELDS = [];
const EMPTY_QUERY_PARAMS = {};

// RecordCard renders using the exact same field.render / renderFieldValue functions
// that the list view table uses — no hardcoded field slots except name (header) and
// action (footer). Every other visible field is a dynamic label + value row.
function RecordCard({
  record,
  fields,
  selected,
  onSelect,
  onPreview,
  onRecordClick,
  isControlled,
  selectionMode = 'checkbox',
  onPopupAction,
  permissions,
}) {
  const nameField = fields.find((f) => f.cardRole === 'name');
  const actionField = fields.find((f) => f.cardRole === 'action');
  const headerActionField = fields.find((f) => f.cardRole === 'headerAction');
  const bodyFields = fields.filter((f) => f !== nameField && f !== actionField && f !== headerActionField);

  const nameValue = nameField ? getRecordValue(record, getFieldValue(nameField)) : '';

  const actionNode = actionField?.render
    ? actionField.render(getRecordRawValue(record, getFieldValue(actionField)), record)
    : null;

  const headerActionNode = headerActionField?.render
    ? headerActionField.render(getRecordRawValue(record, getFieldValue(headerActionField)), record)
    : null;

  return (
    <div
      className={`param-card${selected ? ' param-card--selected' : ''}`}
      aria-selected={selected}
      onClick={(event) => {
        if (isInteractiveClickTarget(event.target)) return;
        onRecordClick?.();
      }}
      role={onRecordClick ? 'button' : undefined}
      tabIndex={onRecordClick ? 0 : undefined}
      onKeyDown={(event) => {
        if (!onRecordClick || (event.key !== 'Enter' && event.key !== ' ')) return;
        event.preventDefault();
        onRecordClick();
      }}
    >

      {/* Header — name always shown prominently; skips field.render (table-specific) */}
      <div className="param-card__header">
        <div className="param-card__name-row">
          {selectionMode === 'checkbox' && (
            <Checkbox
              checked={selected}
              onChange={onSelect}
              onClick={(e) => e.stopPropagation()}
              className="param-card__checkbox"
            />
          )}
          {onPreview && (
            <button
              type="button"
              className="job-row-eye-button"
              aria-label="Preview"
              onClick={(event) => {
                event.stopPropagation();
                onPreview();
              }}
            >
              <img src={eyeOutlinedIcon} alt="" className="job-row-eye-icon" />
            </button>
          )}

          <Text className="param-card__name">{nameValue}</Text>
          {headerActionNode && (
            <div className="param-card__header-actions">{headerActionNode}</div>
          )}
        </div>
      </div>

      {/* Body — every other visible field rendered dynamically, same as list columns */}
      {bodyFields.length > 0 && (
        <div className="param-card__body-fields">
          {bodyFields.map((field) => {
            const fieldKey = getFieldValue(field);
            const rawValue = getRecordRawValue(record, fieldKey);
            const displayNode = isControlled && typeof field.render === 'function'
              ? field.render(rawValue, record)
              : renderFieldValue(field, record, '', onPopupAction, permissions);
            return (
              <div key={fieldKey} className="param-card__field-row">
                <Text className="param-card__field-label">{getFieldLabel(field)}</Text>
                <div className="param-card__field-value">{displayNode}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Divider */}
      <div className="param-card__divider" />

      {/* Footer — action field only */}
      <div className="param-card__footer">
        <div className="param-card__footer-left" />
        <div className="param-card__footer-right">{actionNode}</div>
      </div>
    </div>
  );
}

function hasFilterValue(row) {
  if (row.operator === 'isEmpty' || row.operator === 'notEmpty') return true;
  if (row.value) return true;
  if (row.from || row.to) return true;
  if (Array.isArray(row.values) && row.values.length > 0) return true;
  return false;
}

function formatChipFieldLabel(row) {
  if (row.fieldLabel) return row.fieldLabel;
  return (row.field ?? '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^(.)/, (s) => s.toUpperCase())
    .trim();
}

function formatChipValue(row) {
  if (row.operator === 'isEmpty') return 'Is Empty';
  if (row.operator === 'notEmpty') return 'Not Empty';
  if (row.value) return `"${row.value}"`;
  if (row.from && row.to) return `${row.from} to ${row.to}`;
  if (row.from) return `from ${row.from}`;
  if (row.to) return `to ${row.to}`;
  if (Array.isArray(row.values) && row.values.length > 0) {
    const shown = row.values.slice(0, 2).join(', ');
    return row.values.length > 2 ? `${shown} +${row.values.length - 2}` : shown;
  }
  return '';
}

function FilterChipsBar({ filters, onRemove, onClearAll }) {
  const chips = (filters ?? [])
    .map((row, originalIdx) => ({ row, originalIdx }))
    .filter(({ row }) => row.field && hasFilterValue(row));

  if (chips.length === 0) return null;

  return (
    <Flex gap={6} wrap="wrap" align="center" className="filter-chips-bar">
      <Text className="filter-chips-bar__label">Filters:</Text>
      {chips.map(({ row, originalIdx }) => (
        <Tag
          key={`${row.field}-${originalIdx}`}
          closable
          onClose={() => onRemove(originalIdx)}
          className="filter-chip"
        >
          <span className="filter-chip__field">{formatChipFieldLabel(row)}</span>
          <span className="filter-chip__sep">: </span>
          <span className="filter-chip__value">{formatChipValue(row)}</span>
        </Tag>
      ))}
      {chips.length > 1 && (
        <Button type="link" size="small" onClick={onClearAll} className="filter-chips-clear">
          Clear all
        </Button>
      )}
    </Flex>
  );
}

export default function ListView({
  moduleName,
  authToken,
  actions = [],
  actionRules = [],
  breadcrumbItems = [],
  dataSource = [],
  className = '',
  initialPageSize = 10,
  onAction,
  onSummaryClick,
  rowKey,
  showSummaryButton = false,
  summaryButtonLabel = 'View Summary',
  summaryRoute,
  showFieldButton = true,
  showFilterButton = true,
  fieldButtonLabel = 'View Field',
  fieldButtonRoute,
  tabs,
  tabTitleOverrides = null,
  tableClassName = 'job-list-table',
  // new controlled-mode props
  fields: externalFields = EMPTY_FIELDS,
  fieldOverrides = null,
  extraFields = EMPTY_FIELDS,
  listName = '',
  loading: propLoading = false,
  total: externalTotal,
  current: externalCurrent,
  pageSize: externalPageSize,
  onPageChange,
  onPageSizeChange,
  onSearch,
  toolbarExtra = null,
  showSearch = true,
  searchPlaceholder = 'Min 3 Chars, press Enter to search',
  // Restrict the text search to these field keys (e.g. ['jobTitle',
  // 'clientName']). Empty means search across all fields (default).
  searchFields = EMPTY_FIELDS,
  showViewToggle = false,
  activeView = 'list',
  onViewChange,
  autoCardForExternalRows = true,
  createAction,
  onRecordClick,
  selectedRecordId,
  selectionMode = 'checkbox',
  compact = false,
  onSelectionChange,
  // Source tab props
  selectedSource,
  onTabChange,
  fetchData = getModuleDataList,
  responseMapper = mapListResponse,
  // Extra static params forwarded to fetchData/the module-data-list API
  // (e.g. { assessmentId: recordId }) — merged into the request alongside
  // scope/sort/search/filters, for nested lists scoped to a parent record.
  queryParams = EMPTY_QUERY_PARAMS,
  // Number of columns shown by default (before the user opts more in via the
  // column-visibility menu). Defaults to 6; pass a different value per module
  // when needed.
  defaultVisibleColumnCount = 6,
  // Set to false to hide the Add icon/button for pages that shouldn't allow
  // creating new records from this list (e.g. read-only or nested views).
  showAdd = true,
  lockOuterPageScroll = false,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // Dot-notation module names ("job.client") drill into nested data of the
  // base module — permissions, create routes and role checks belong to the
  // base module, not the nested path.
  const baseModuleName = getBaseModuleName(moduleName);
  const [terminalClientStatusIds, setTerminalClientStatusIds] = useState(() => new Set());
  const [terminalClientStatusNames, setTerminalClientStatusNames] = useState(() => new Set());

  useEffect(() => {
    if (normalizeComparableText(baseModuleName) !== 'submissions') {
      setTerminalClientStatusIds(new Set());
      setTerminalClientStatusNames(new Set());
      return undefined;
    }

    let cancelled = false;
    const params = new URLSearchParams({
      masterName: 'clientSubmissions',
      offset: '0',
      limit: '100',
    });
    if (APP_ID) params.set('app_id', APP_ID);

    fetchJsonWithAuth(AUTH_URL, `/masters/dropdown?${params.toString()}`)
      .then((response) => {
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        const terminalRows = rows.filter((row) => row?.isTerminalStatus === true);
        setTerminalClientStatusIds(new Set(
          terminalRows
            .map((row) => Number(row?.id ?? row?.uniqueId ?? 0))
            .filter((id) => id > 0),
        ));
        setTerminalClientStatusNames(new Set(
          terminalRows
            .map((row) => normalizeComparableText(row?.name))
            .filter(Boolean),
        ));
      })
      .catch(() => {
        if (cancelled) return;
        setTerminalClientStatusIds(new Set());
        setTerminalClientStatusNames(new Set());
      });

    return () => {
      cancelled = true;
    };
  }, [baseModuleName]);
  const shouldLockOuterPageScroll = Boolean(lockOuterPageScroll);
  const { canView, canAdd, canEdit, canDelete } = useMenuPermission(baseModuleName);
  const { can } = usePermissions();
  // Callers often pass queryParams as a fresh inline object each render
  // (e.g. queryParams={{ assessmentId: recordId }}) — memoize by value so the
  // fetch effect below only re-runs when the actual params change.
  const queryParamsKey = JSON.stringify(queryParams ?? EMPTY_QUERY_PARAMS);
  const stableQueryParams = useMemo(() => queryParams ?? EMPTY_QUERY_PARAMS, [queryParamsKey]);
  const [searchParams] = useSearchParams();
  const routePage = getPageFromSearchParams(searchParams);
  // Seed the active tab from `selectedSource` instead of always starting on
  // 'all'.
  //
  // The tab drives `activeTabScope`, which is a dependency of the list fetch.
  // Starting on 'all' meant the FIRST fetch went out with an empty scope, and
  // the sync effect below then corrected the tab once the response's `tabs`
  // arrived — firing a SECOND, identical-but-correctly-scoped fetch. Every list
  // page therefore issued two /module-data-list calls on open, and the first of
  // them asked the wrong question: measured on Jobs, an unscoped 648ms query
  // whose result was thrown away, followed by the real scope=myJobs one.
  //
  // Every consumer passes a route TAB KEY here (jobs `/jobs/:tab`, submissions
  // `/submissions/:tab`, campaign contacts), and internally `selectedSource` is
  // only ever compared against tab keys — so trusting it for the first render is
  // exactly as correct as the value the sync effect was going to arrive at.
  // The sync still runs and still reconciles against the real tabs, so a value
  // that turns out not to match any tab is corrected exactly as before.
  const [activeTab, setActiveTab] = useState(() => {
    const seed = String(selectedSource ?? '').trim();
    return seed || 'all';
  });
  const [search, setSearch] = useState('');
  const [committedSearch, setCommittedSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [appliedFilterRows, setAppliedFilterRows] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [pagination, setPagination] = useState({ current: routePage, pageSize: initialPageSize });
  const [sortConfig, setSortConfig] = useState({ sort: '', sortDir: '' });
  const [visibleColumnKeys, setVisibleColumnKeys] = useState([]);
  const [columnMenuOpen, setColumnMenuOpen] = useState(false);
  const [selectedCardIds, setSelectedCardIds] = useState([]);
  useEffect(() => {
    if (authToken) setAuthTokenOverride(authToken);
    return () => {
      if (authToken) clearAuthTokenOverride();
    };
  }, [authToken]);

  useEffect(() => {
    if (!shouldLockOuterPageScroll) return undefined;

    document.documentElement.classList.add('listview-page-scroll-lock');
    document.body.classList.add('listview-page-scroll-lock');

    return () => {
      document.documentElement.classList.remove('listview-page-scroll-lock');
      document.body.classList.remove('listview-page-scroll-lock');
    };
  }, [shouldLockOuterPageScroll]);

  // Tracks the live width of the table's wrapping element so column widths
  // can be recalculated as the viewport / sidebar / container resizes,
  // instead of being frozen at whatever they measured on first render.
  const tableContainerRef = useRef(null);
  const [tableContainerWidth, setTableContainerWidth] = useState(0);
  const syncedSourceRef = useRef();
  const skipNextImplicitScopeFetchRef = useRef('');

  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width;
      if (typeof width === 'number') setTableContainerWidth(width);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const resolveRecordId = (record, index) => String(getRowKey(record, index, rowKey));
  const isRecordSelected = (record, index) => (
    String(resolveRecordId(record, index)) === String(selectedRecordId ?? '')
  );

  const handleRecordClick = (record, index) => {
    const id = resolveRecordId(record, index);
    if (!id) return;
    onRecordClick?.(record, id);
  };

  const updateSelection = (keys, rows) => {
    setSelectedRowKeys(keys);
    setSelectedRows(rows);
    onSelectionChange?.(keys, rows);
  };
  const [moduleRows, setModuleRows] = useState(dataSource);
  const [moduleTotal, setModuleTotal] = useState(dataSource.length);
  const [moduleTabs, setModuleTabs] = useState([]);
  const [moduleFields, setModuleFields] = useState([]);
  const [moduleTabField, setModuleTabField] = useState('');
  const [moduleActions, setModuleActions] = useState([]);
  const [moduleActionRules, setModuleActionRules] = useState([]);
  const [moduleLoading, setModuleLoading] = useState(false);
  // Whether the module list API has answered at least once. `moduleLoading`
  // cannot stand in for this: it initialises false and only flips inside the
  // fetch effect, which runs after the first paint — so on that first frame
  // "not loading" and "never loaded" look identical. `listTabs` uses this to
  // keep tab counts hidden until there is a real response to report, instead of
  // flashing a "0" built from the not-yet-populated total. Never reset: once a
  // module has answered, its tabs keep reporting numbers.
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [moduleError, setModuleError] = useState(null);
  const [defaultListSnapshot, setDefaultListSnapshot] = useState(null);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const isControlled = Boolean(onPageChange || onPageSizeChange || onSearch);

  // Controlled mode overrides - must be declared before useFieldConfig hook
  const effectiveRows = isControlled ? dataSource : moduleRows;
  const effectiveTotal = isControlled ? (externalTotal ?? 0) : moduleTotal;
  const effectiveFields = isControlled ? externalFields : moduleFields;
  const effectiveTabs = isControlled ? tabs : moduleTabs;
  const effectiveTabField = isControlled ? '' : moduleTabField;
  const effectiveError = isControlled ? null : moduleError;
  const hasExternalSourceRows = useMemo(
    () => effectiveRows.some((record) => isExternalSourceRecord(record)),
    [effectiveRows],
  );
  const hasExternalSourceFields = useMemo(
    () => effectiveFields.some((field) => isExternalSourceField(field)),
    [effectiveFields],
  );
  // External-source rows (Dice and friends) carry a wide, sparse field set that
  // reads badly in a table, so this OVERRIDES the caller's activeView and shows
  // cards. It is a hard override, not a default: a caller that asks for a list
  // does not get one, and the view toggle renders but does nothing.
  //
  // Kept ON by default so every existing caller behaves exactly as before. A
  // caller that owns its own view — the candidate sourcing screen, whose Dice
  // tab must open as a list and whose toggle must work — turns it off.
  const resolvedActiveView =
    (autoCardForExternalRows && (hasExternalSourceRows || hasExternalSourceFields))
      ? 'card'
      : activeView;
  const configuredActions = actions.length > 0 ? actions : moduleActions;
  const configuredActionRules = actionRules.length > 0 ? actionRules : moduleActionRules;
  const actionItems = useMemo(() => normalizeActions(configuredActions), [configuredActions]);

  const actionByKey = useMemo(() => (
    new Map(flattenActions(actionItems).map((action) => [action.key, action]))
  ), [actionItems]);
  const visibleActionItems = useMemo(() => (
    filterActionTree(actionItems, (action) => {
      // Infer permission key: explicit > danger convention > key name
      const pk = getCrudPermissionKey(action);
      if (pk === 'add' && !canAdd) return false;
      if (pk === 'edit' && !canEdit) return false;
      if (pk === 'delete' && !canDelete) return false;
      if (!checkPermission(can, action.permission, baseModuleName)) return false;
      if (action.showInList === false) return false;
      if (!action.allowedTabs) return true;
      return action.allowedTabs.some((tab) => normalizeComparableText(tab) === normalizeComparableText(activeTab));
    })
  ), [actionItems, activeTab, can, canAdd, canEdit, canDelete, baseModuleName]);
  const selectedActionItems = useMemo(() => filterActionTree(visibleActionItems, (action) => (
    (selectedRowKeys.length === 1 || action.allowMultiple || action.danger)
    && selectedRecordsAllowAction(action, selectedRows)
    && !(
      normalizeComparableText(baseModuleName) === 'submissions'
      && isChangeStatusAction(action)
      && selectedRecordsHaveTerminalClientStatus(
        selectedRows,
        terminalClientStatusIds,
        terminalClientStatusNames,
      )
    )
    && !isActionBlocked(action.key, selectedRows, configuredActionRules, activeTab)
  )), [
    activeTab,
    baseModuleName,
    configuredActionRules,
    selectedRowKeys.length,
    selectedRows,
    terminalClientStatusIds,
    terminalClientStatusNames,
    visibleActionItems,
  ]);

  const hasModuleName = typeof moduleName === 'string'
    ? moduleName.trim().length > 0
    : Boolean(moduleName);
  const createRoute = hasModuleName
    ? `/${baseModuleName}-create${createAction ? `?action=${encodeURIComponent(createAction)}` : ''}`
    : '';
  useEffect(() => {
    if (hasModuleName && !canView) {
      navigate('/', { replace: true });
    }
  }, [canView, hasModuleName, navigate]);

  const updatePageRoute = (page, { replace = false } = {}) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    removePageSearchParams(nextSearchParams);

    const baseSearch = nextSearchParams.toString();
    const pageSearch = page > 1 ? `p${page}` : '';
    const nextSearch = [baseSearch, pageSearch].filter(Boolean).join('&');
    const currentSearch = location.search.replace(/^\?/, '');

    if (nextSearch !== currentSearch) {
      navigate(
        {
          pathname: location.pathname,
          search: nextSearch ? `?${nextSearch}` : '',
        },
        { replace },
      );
    }
  };

  const setPaginationPage = (page, options) => {
    setPagination((current) => ({ ...current, current: page }));
    updatePageRoute(page, options);
  };

  const resetPaginationPage = () => {
    setPaginationPage(1, { replace: true });
  };

  const { config, loading: configLoading, error } = useFieldConfig(!isControlled && hasModuleName ? moduleName : null);

  const handleSortChange = useCallback((fieldKey) => {
    const sameField = sortConfig.sort === fieldKey;
    const nextSortDir = getNextSortDirection(sortConfig.sortDir, sameField);
    const nextSort = nextSortDir ? String(fieldKey) : '';

    setSortConfig({
      sort: nextSort,
      sortDir: nextSortDir,
    });

    if (typeof onSearch === 'function') {
      onSearch(nextSort, nextSortDir);
    }

    resetPaginationPage();
  }, [onSearch, resetPaginationPage, sortConfig.sort, sortConfig.sortDir]);

  useEffect(() => {
    setPagination((current) => (
      current.current === routePage ? current : { ...current, current: routePage }
    ));
  }, [routePage]);

  useEffect(() => {
    if (!isControlled) return;

    setPagination((current) => {
      const nextCurrent = getPositivePage(externalCurrent ?? current.current);
      const nextPageSize = Number(externalPageSize) > 0 ? Number(externalPageSize) : current.pageSize;

      return current.current === nextCurrent && current.pageSize === nextPageSize
        ? current
        : { current: nextCurrent, pageSize: nextPageSize };
    });
  }, [externalCurrent, externalPageSize, isControlled]);

  const refreshTimestamp = location.state?.refreshTimestamp;
  useEffect(() => {
    if (!refreshTimestamp) return;
    updateSelection([], []);
    setRefreshKey((k) => k + 1);
  }, [refreshTimestamp]);

  const activeTabConfig = useMemo(() => {
    const availableTabs = [
      ...(Array.isArray(tabs) ? tabs : []),
      ...moduleTabs,
    ];

    const normalizedActiveTab = normalizeTabKey(activeTab);
    return availableTabs.find((tab, index) => normalizeTabKey(getTabKey(tab, index)) === normalizedActiveTab);
  }, [activeTab, moduleTabs, tabs]);
  const activeTabField = activeTabConfig?.field
    ?? activeTabConfig?.fieldName
    ?? activeTabConfig?.filterField
    ?? activeTabConfig?.dataIndex
    ?? moduleTabField;
  const activeTabScope = isAllTabKey(activeTab) ? '' : activeTab;
  const canFilterActiveTab = !activeTabScope
    && !isAllTabKey(activeTab)
    && normalizeComparableText(activeTabField).length > 0;
  const hasActiveSearch = committedSearch.trim().length >= 3;
  // Committed term sent to the API (server-side search across visible +
  // lookup-resolved fields, e.g. clientName); empty until 3+ chars committed.
  const activeSearchTerm = hasActiveSearch ? committedSearch.trim() : '';
  const hasActiveFieldFilters = appliedFilterRows.some((row) => row.field);
  const defaultListSnapshotKey = getDefaultListSnapshotKey({
    moduleName,
    activeTabScope,
    pageSize: pagination.pageSize,
    sort: sortConfig.sort,
    sortDir: sortConfig.sortDir,
    queryParams: stableQueryParams,
  });
  const hasUsableDefaultSnapshot = pagination.current === 1 && defaultListSnapshot?.key === defaultListSnapshotKey;
  const knownDefaultTotal = Number(defaultListSnapshot?.total);
  const searchFetchTotal = hasActiveSearch
    ? (
      Number.isFinite(knownDefaultTotal) && knownDefaultTotal > 0
        ? knownDefaultTotal
        : moduleTotal
    )
    : 0;

  useEffect(() => {
    if (isControlled) return undefined;

    if (!hasModuleName) {
      setModuleRows([]);
      setModuleTotal(0);
      setModuleTabs([]);
      setModuleFields([]);
      setModuleTabField('');
      setModuleActions([]);
      setModuleActionRules([]);
      setModuleError(null);
      return undefined;
    }

    const normalizedActiveTabScope = normalizeTabKey(activeTabScope);
    if (
      skipNextImplicitScopeFetchRef.current
      && skipNextImplicitScopeFetchRef.current === normalizedActiveTabScope
    ) {
      skipNextImplicitScopeFetchRef.current = '';
      return undefined;
    }

    let cancelled = false;
    // Fetch all rows only when a local search is active so it can match across
    // the full result set. Field filters are handled server-side, so normal
    // pagination applies even while they're active.
    const shouldFetchAllRows = hasActiveSearch && searchFetchTotal > pagination.pageSize;
    const limit = shouldFetchAllRows ? searchFetchTotal : pagination.pageSize;
    const offset = shouldFetchAllRows ? 0 : (pagination.current - 1) * pagination.pageSize;
    setModuleLoading(!(!hasActiveSearch && !hasActiveFieldFilters && pagination.current === 1 && hasUsableDefaultSnapshot));
    setModuleError(null);
    fetchData(moduleName, limit, offset, {
      scope: activeTabScope,
      sort: sortConfig.sort,
      sortDir: sortConfig.sortDir,
      search: activeSearchTerm,
      filters: appliedFilterRows,
      queryParams: stableQueryParams,
    })
      .then((response) => {
        if (cancelled) return;
        const {
          rows,
          total,
          tabs: responseTabs,
          fields: responseFields,
          tabField,
          actions: responseActions,
          actionRules: responseActionRules,
        } = responseMapper(response, moduleName);
        const shouldAdoptImplicitScope = (
          !selectedSource
          && isAllTabKey(activeTab)
          && Array.isArray(responseTabs)
          && responseTabs.length > 0
          && !responseTabs.some((tab, index) => isAllTabKey(getTabKey(tab, index)))
        );
        const implicitScopeTabKey = shouldAdoptImplicitScope
          ? getTabKey(responseTabs[0], 0)
          : '';
        const snapshotScope = implicitScopeTabKey ? String(implicitScopeTabKey) : activeTabScope;
        const snapshotKey = getDefaultListSnapshotKey({
          moduleName,
          activeTabScope: snapshotScope,
          pageSize: pagination.pageSize,
          sort: sortConfig.sort,
          sortDir: sortConfig.sortDir,
          queryParams: stableQueryParams,
        });

        setModuleRows(rows);
        setModuleTotal(total);
        setModuleTabs(responseTabs);
        setModuleFields(responseFields);
        setModuleTabField(tabField);
        setModuleActions(responseActions);
        setModuleActionRules(responseActionRules);

        if (implicitScopeTabKey) {
          skipNextImplicitScopeFetchRef.current = normalizeTabKey(implicitScopeTabKey);
          setActiveTab((current) => (
            normalizeTabKey(current) === normalizeTabKey(implicitScopeTabKey)
              ? current
              : String(implicitScopeTabKey)
          ));
        }

        if (!hasActiveSearch && !hasActiveFieldFilters && pagination.current === 1) {
          setDefaultListSnapshot({
            key: snapshotKey,
            rows,
            total,
            tabs: responseTabs,
            fields: responseFields,
            tabField,
            actions: responseActions,
            actionRules: responseActionRules,
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setModuleRows([]);
        setModuleTotal(0);
        setModuleTabs([]);
        setModuleFields([]);
        setModuleTabField('');
        setModuleActions([]);
        setModuleActionRules([]);
        setModuleError(err.message);
        if (hasModuleName) {
          message.error('Invalid module name or data could not be loaded.');
        }
      })
      .finally(() => {
        if (cancelled) return;
        setModuleLoading(false);
        // Set on failure too: the catch above clears moduleTabs, and the
        // placeholder tab reporting 0 matches the "No data found" empty state
        // rendered below it. A bare tab there would read as "still loading".
        setHasLoadedOnce(true);
      });

    return () => {
      cancelled = true;
    };
  }, [activeSearchTerm, activeTab, activeTabScope, appliedFilterRows, fetchData, hasActiveFieldFilters, hasActiveSearch, hasModuleName, isControlled, moduleName, pagination.current, pagination.pageSize, refreshKey, responseMapper, searchFetchTotal, selectedSource, sortConfig.sort, sortConfig.sortDir, stableQueryParams]);

  const normalizedFields = useMemo(() => {
    const usesExternalFields = isControlled && Array.isArray(externalFields) && externalFields.length;
    const usesModuleFields = !isControlled && Array.isArray(moduleFields) && moduleFields.length;
    const usesConfigFields = !error && Array.isArray(config) && config.length;

    const fieldSource = usesExternalFields
      ? externalFields
      : usesModuleFields
        ? moduleFields
        : (usesConfigFields ? config : []);

    const moduleFieldByKey = new Map(
      moduleFields
        .map((field) => [getFieldValue(field), field])
        .filter(([key]) => key)
    );

    const normalizedBase = fieldSource
      .map((field) => {
        const key = getFieldValue(field);
        const moduleField = moduleFieldByKey.get(key) ?? {};
        const override = key && fieldOverrides && typeof fieldOverrides === 'object' ? fieldOverrides[key] : null;
        const renderConfig = override?.renderConfig
          ? { ...(moduleField.renderConfig ?? field.renderConfig ?? {}), ...override.renderConfig }
          : (field.renderConfig ?? moduleField.renderConfig ?? null);

        return {
          ...moduleField,
          ...field,
          ...(override ?? {}),
          ...(renderConfig ? { renderConfig } : {}),
          key,
          label: getFieldLabel(override ?? field) ?? getFieldLabel(field) ?? getFieldLabel(moduleField),
        };
      })
      .filter((field) => field.key && field.label);

    const normalizedExtra = (Array.isArray(extraFields) ? extraFields : [])
      .map((field, index) => {
        const key = getFieldValue(field);
        if (!key) return null;

        return {
          ...field,
          key,
          label: getFieldLabel(field) ?? `Field ${index + 1}`,
          isVirtual: field.isVirtual ?? true,
          isVisible: typeof field.isVisible === 'boolean' ? field.isVisible : true,
        };
      })
      .filter(Boolean);

    return getOrderedFields([...normalizedBase, ...normalizedExtra]);
  }, [config, error, externalFields, extraFields, fieldOverrides, isControlled, moduleFields]);

  const tableFields = useMemo(() => (
    normalizedFields
      .filter((field) => {
        if (typeof field.isVisible === 'boolean') return field.isVisible;
        if (typeof field.isVisible === 'string') return field.isVisible.toLowerCase() === 'true';
        return true;
      })
      .map((field) => ({
        key: field.key,
        label: field.label,
      }))
  ), [normalizedFields]);

  const tableFieldKeys = tableFields.map((field) => field.key);
  const visibleTableColumnKeys = visibleColumnKeys.filter((key) => tableFieldKeys.includes(key));
  const visibleKeys = visibleTableColumnKeys.length
    ? visibleTableColumnKeys
    : tableFields.slice(0, defaultVisibleColumnCount).map((field) => field.key);
  const minimumVisibleColumns = Math.min(defaultVisibleColumnCount, tableFields.length);
  const hasHorizontalScroll = visibleKeys.length > defaultVisibleColumnCount;

  // Card view: tableHidden fields always show (they exist for cards only);
  // all other fields respect the same visibility as list columns.
  const visibleCardFields = useMemo(() => (
    normalizedFields.filter((field) => !field.cardHidden && (field.tableHidden || visibleKeys.includes(field.key)))
  ), [normalizedFields, visibleKeys]);

  const filteredData = useMemo(() => {
    const q = search.toLowerCase();
    const sourceData = effectiveRows;
    const tabbedData = canFilterActiveTab
      ? sourceData.filter((record) => (
        recordMatchesTab(record, activeTab, activeTabConfig, normalizedFields, activeTabField)
      ))
      : sourceData;

    // When searchFields is provided, only those field keys participate in the
    // text search; otherwise every configured field is searchable.
    const searchableFields = searchFields.length
      ? normalizedFields.filter((field) => searchFields.includes(field.key))
      : normalizedFields;

    const searchedData = !q || q.length < 3
      ? tabbedData
      : tabbedData.filter((record) => (
        searchableFields.some((field) => (
          String(getRecordValue(record, field.key)).toLowerCase().includes(q)
          // Secondary display values (e.g. the client name shown under the
          // job title) must be searchable too, not just the column's own key.
          || getSecondaryFieldKeys(field).some((secondaryKey) => (
            String(getRecordValue(record, secondaryKey)).toLowerCase().includes(q)
          ))
        ))
      ));

    // Field filters are evaluated server-side (sent via the API call); only
    // the local text search needs client-side narrowing here.
    return searchedData;
  }, [activeTab, activeTabConfig, activeTabField, canFilterActiveTab, effectiveRows, normalizedFields, search, searchFields]);

  const pagedFilteredData = useMemo(() => {
    if (!hasActiveSearch) return filteredData;

    const start = (pagination.current - 1) * pagination.pageSize;
    return filteredData.slice(start, start + pagination.pageSize);
  }, [filteredData, hasActiveSearch, pagination.current, pagination.pageSize]);

  // When searching locally, use the client-filtered count. Otherwise the API
  // already returns the correct total (accounting for server-side filters).
  const tableTotal = hasActiveSearch ? filteredData.length : effectiveTotal;
  const showSearchResultsCount = committedSearch.trim().length >= 3;
  const searchResultsCount = String(filteredData.length).padStart(2, '0');
  const hasSourceRows = effectiveTotal > 0 || effectiveRows.length > 0;
  const shouldShowSearchInput = hasSourceRows || search.trim().length > 0 || committedSearch.trim().length > 0;
  const emptyMessage = !hasModuleName
    ? 'Please provide a module name to view data.'
    : moduleError
      ? 'Invalid module name or data could not be loaded.'
      : error
        ? 'Field configuration failed to load.'
        : 'No data found.';
  const normalizedTabTitleOverrides = useMemo(() => {
    if (!tabTitleOverrides || typeof tabTitleOverrides !== 'object') return null;

    return Object.entries(tabTitleOverrides).reduce((acc, [key, value]) => {
      if (value === null || value === undefined) return acc;
      acc[String(key)] = String(value);
      return acc;
    }, {});
  }, [tabTitleOverrides]);
  const listTabs = useMemo(() => {
    const providedTabs = Array.isArray(tabs) ? tabs : [];
    // Counts are only normalised for API-driven tabs. A caller that passes
    // `tabs` owns their counts: buildSourceTabs (sourceProviderActions.js) omits
    // `count` to mean "this source has not been searched yet", which is not the
    // same as zero, and forcing a 0 there would label it "no results".
    const isApiDriven = !providedTabs.length;
    const baseTabs = providedTabs.length
      ? providedTabs
      : moduleTabs.length
        ? moduleTabs
        : [{ key: 'all', title: moduleName ?? 'List', count: tableTotal }];

    const hasTitleOverrides = Boolean(
      normalizedTabTitleOverrides && Object.keys(normalizedTabTitleOverrides).length
    );

    if (!isApiDriven && !hasTitleOverrides) return baseTabs;

    return baseTabs.map((tab, index) => {
      if (!tab || typeof tab !== 'object') return tab;

      const overrideTitle = hasTitleOverrides
        ? normalizedTabTitleOverrides[String(getTabKey(tab, index))]
        : undefined;

      if (!overrideTitle && !isApiDriven) return tab;

      const next = { ...tab };

      if (overrideTitle) next.title = overrideTitle;

      if (isApiDriven) {
        if (hasLoadedOnce) {
          // The response has landed, so every tab reports a real number. A tab
          // the backend sent without a count is empty and reads "0" — a blank
          // badge is indistinguishable from "still loading" and leaves an empty
          // tab invisible from every other tab. Number(null) and Number('') are
          // both 0; only a genuinely absent count falls through to the default.
          const numericCount = Number(tab.count ?? tab.countValue);
          next.count = Number.isFinite(numericCount) ? numericCount : 0;
        } else {
          // Nothing has come back yet — any count here would be a guess. Clear
          // both keys, since DynamicTabs reads `count ?? countValue` and leaving
          // either one would resurface a badge.
          delete next.count;
          delete next.countValue;
        }
      }

      return next;
    });
  }, [hasLoadedOnce, moduleName, moduleTabs, normalizedTabTitleOverrides, tableTotal, tabs]);

  useEffect(() => {
    if (!listTabs.length) return;

    // Only reconcile against REAL tabs. Until the first response lands,
    // `listTabs` is a synthesised placeholder (a lone "all"), and treating that
    // as authoritative made this guard fight the tab the page actually asked
    // for: it saw "myJobs" was not in ["all"], reset to "all", and the sync
    // effect below then put "myJobs" back once the true tabs arrived — a third
    // fetch, with the middle one unscoped and discarded.
    const hasRealTabs = (moduleTabs?.length ?? 0) > 0 || (Array.isArray(tabs) && tabs.length > 0);
    if (!hasRealTabs) return;

    const normalizedActiveTab = normalizeTabKey(activeTab);
    const activeTabExists = listTabs.some((tab, index) => normalizeTabKey(getTabKey(tab, index)) === normalizedActiveTab);

    if (!activeTabExists) {
      const firstTabKey = getTabKey(listTabs[0], 0);
      if (firstTabKey) setActiveTab(String(firstTabKey));
    }
  }, [activeTab, listTabs, moduleTabs, tabs]);

  // Tracks the last `selectedSource` value already synced into `activeTab`,
  // so this effect only reacts to genuine external changes (a parent driving
  // the active tab from elsewhere) instead of re-running on every internal
  // activeTab change. It used to depend on `activeTab` and re-check against
  // `selectedSource` on every click; if the consumer's `onTabChange` didn't
  // echo `selectedSource` back in the same tick, that comparison would see
  // the optimistic click as "wrong" and immediately snap it back to the
  // stale prop, making tab clicks look like they did nothing.
  useEffect(() => {
    if (selectedSource === undefined || !listTabs.length) return;

    const matchingTab = listTabs.find((tab, index) => (
      normalizeTabKey(getTabKey(tab, index)) === normalizeTabKey(selectedSource)
    ));

    // During the first render the API-backed tabs may not be loaded yet and
    // ListView can temporarily contain only its fallback tab. Do not cache the
    // URL/parent-selected source until the corresponding real tab exists.
    if (!matchingTab) return;

    const nextActiveTab = String(getTabKey(matchingTab, listTabs.indexOf(matchingTab)));

    if (
      selectedSource === syncedSourceRef.current
      && normalizeTabKey(activeTab) === normalizeTabKey(nextActiveTab)
    ) {
      return;
    }

    syncedSourceRef.current = selectedSource;
    setActiveTab((current) => (
      normalizeTabKey(current) === normalizeTabKey(nextActiveTab)
        ? current
        : nextActiveTab
    ));
  }, [activeTab, listTabs, selectedSource]);

  const handlePreviewOpen = (record) => {
    // History's pushState needs serializable state — strip any
    // function-valued field properties (e.g. a card's custom `render`)
    // before handing normalizedFields to navigate(), or a field with a
    // real function attached throws DataCloneError and crashes the page.
    const serializableFields = normalizedFields.map((field) => {
      const clean = {};
      Object.entries(field).forEach(([key, value]) => {
        if (typeof value !== 'function') clean[key] = value;
      });
      return clean;
    });
    navigate('/popup/preview', {
      state: {
        background: location,
        moduleName: baseModuleName,
        record,
        fields: serializableFields,
      },
    });
  };

  // openRowPopup — fires a per-row "popup" action button (Admin > Users >
  // List View > Action Buttons, type="popup"), reusing the exact route-based
  // modal mechanism the toolbar bulk actions use (see handleActionClick's
  // action.route branch below), just scoped to this single record instead of
  // the current multi-row selection.
  const openRowPopup = (routeUrl, record, action = {}) => {
    const resolvedRoute = fillLinkTemplate(routeUrl, record);
    const normalizedRoute = resolvedRoute.startsWith('/') ? resolvedRoute : `/${resolvedRoute}`;
    const recordId = record?._id ?? record?.id;

    navigate(normalizedRoute, {
      state: {
        background: location,
        selectedIds: recordId !== undefined ? [recordId] : [],
        selectedRecords: [record],
        moduleName: baseModuleName,
        popupContext: action.popupState ?? {},
        popupAction: {
          key: action.key,
          apiMethod: action.apiMethod,
          apiPath: action.apiPath,
          defaultTemplateId: action.defaultTemplateId,
          requireJobId: action.requireJobId,
          popupKey: action.popupKey,
          actionType: action.actionType ?? action.type,
          popupFieldOverrides: action.popupFieldOverrides,
        },
        requireJobId: action.requireJobId,
        ...action.popupState,
      },
    });
  };

  const columns = useMemo(() => {
    const iconColumn = {
      dataIndex: 'icons',
      key: 'icons',
      visibilityKey: 'jobsGroup',
      fixed: 'left',
      width: 25,
      render: (_, record) => (
        <Space size={4} className="job-row-icons">
          <button
            type="button"
            className="job-row-eye-button"
            aria-label="Preview job"
            onClick={() => handlePreviewOpen(record)}
          >
            <img src={eyeOutlinedIcon} alt="" className="job-row-eye-icon" />
          </button>

          {record.hasLinkedIn && (
            <Tooltip title="LinkedIn">
              <LinkedinFilled className="job-row-linkedin-icon" />
            </Tooltip>
          )}
        </Space>
      ),
    };

    const dataColumns = normalizedFields
      .filter((field) => visibleKeys.includes(field.key))
      .map((field, idx) => {
        const sortDir = sortConfig.sort === field.key ? sortConfig.sortDir : '';

        return {
          title: renderSortHeader({
            label: field.label,
            sortDir,
            sortable: hasSourceRows,
            onSort: () => handleSortChange(field.key),
          }),
          dataIndex: field.key,
          key: field.key,
          fixed: idx === 0 ? 'left' : undefined,
          ellipsis: { showTitle: false },
          width: estimateColumnWidth(field, pagedFilteredData),
          render: (_, record) => renderFieldValue(field, record, committedSearch, openRowPopup, { canAdd, canEdit, canDelete }, configuredActionRules, activeTabScope),
        };
      });

    // Grow the data columns to fill any leftover horizontal space instead of
    // leaving a gap after the last column — the extra width is shared out in
    // proportion to each column's own content-based width, so on wide
    // screens (or pages with only a handful of columns) every column widens
    // together rather than the table stopping short of the container edge.
    // Only applies when the table isn't already horizontally scrolling
    // (scroll.x unset, columns fit the viewport) and the container has been
    // measured.
    if (!(hasHorizontalScroll || isControlled) && tableContainerWidth > 0 && dataColumns.length > 0) {
      // Reserve room for the table's own vertical scrollbar (it eats into
      // the table's content width from the inside, after tableContainerWidth
      // was measured on the outer wrapper) — otherwise columns are sized to
      // the full wrapper width, overflow by the scrollbar's width, and a
      // fixed column then forces an unwanted horizontal scrollbar just to
      // reach those last few pixels.
      const reservedWidth = iconColumn.width
        + (hasSourceRows && selectionMode === 'checkbox' ? 48 : 0)
        + VERTICAL_SCROLLBAR_GUTTER_PX;
      const naturalTotal = dataColumns.reduce((sum, col) => sum + col.width, 0);
      const availableWidth = tableContainerWidth - reservedWidth - naturalTotal;

      if (availableWidth > 0 && naturalTotal > 0) {
        dataColumns.forEach((col) => {
          col.width = Math.floor(col.width + (availableWidth * (col.width / naturalTotal)));
        });
      }
    }

    return [
      iconColumn,
      ...dataColumns,
    ];
  }, [activeTab, canAdd, canDelete, canEdit, committedSearch, configuredActionRules, handleSortChange, hasHorizontalScroll, hasSourceRows, isControlled, location, moduleName, navigate, normalizedFields, pagedFilteredData, selectionMode, sortConfig.sort, sortConfig.sortDir, tableContainerWidth, visibleActionItems, visibleKeys]);

  const handleTabChangeInternal = useCallback((key) => {
    const selectedTab = listTabs.find((tab, index) => normalizeTabKey(getTabKey(tab, index)) === normalizeTabKey(key));
    const nextKey = String(selectedTab ? getTabKey(selectedTab, listTabs.indexOf(selectedTab)) : key);

    setActiveTab(nextKey);
    setSelectedRowKeys([]);
    setSelectedRows([]);
    resetPaginationPage();

    // Tab syncing (selectedSource/onTabChange) is its own controlled-mode
    // concern, independent of pagination — gating this on `isControlled`
    // (which only reflects onPageChange/onPageSizeChange/onSearch) meant a
    // consumer could pass selectedSource for tab tracking without pagination
    // control and never get notified, or the reverse. Call it whenever it's
    // actually provided.
    if (typeof onTabChange === 'function') {
      const newSource = selectedTab?.key ?? selectedTab?.value ?? selectedTab?.id ?? nextKey;
      // Mark this as already-synced so the selectedSource effect above
      // doesn't treat the parent's echoed-back value as a fresh external
      // change and re-run redundantly.
      syncedSourceRef.current = newSource;
      onTabChange(newSource);
    }
  }, [onTabChange, listTabs, resetPaginationPage]);

  // handleActionClickForRecords is the action-execution body, parameterized
  // over which ids/records it acts on. handleActionClick (the bulk toolbar
  // handler) and the per-row action column both delegate to it, so a single
  // row's action click behaves exactly like selecting just that one row.
  // Declared as a function (not `const fn = () => {}`) so it's hoisted within
  // this component body — the columns useMemo above references
  // handleRowActionClick, which delegates here, even though this definition
  // appears later in source order.
  function handleActionClickForRecords(key, ids, records) {
    const action = actionByKey.get(String(key));

    if (!action) return;
    // Group actions (e.g. "Shortlist") only render a submenu of their
    // children — clicking the group itself is a no-op.
    if (action.actionType === 'group') return;

    if (typeof onAction === 'function') {
      onAction({ key: action.key, label: action.label, selectedIds: ids, selectedRecords: records });
    }

    if (typeof action.onClick === 'function') {
      action.onClick({ action, selectedIds: ids, selectedRecords: records });
      return;
    }
    if (action.actionType === 'copy') {
      const values = records
        .map((record) => getNestedRecordValue(record, action.copyField || 'id') ?? record?._id)
        .filter((value) => value !== undefined && value !== null);
      navigator.clipboard.writeText(values.join('\n'))
        .then(() => message.success('Copied to clipboard'))
        .catch(() => message.error('Could not copy to clipboard'));
      return;
    }
    if (action.actionType === 'api' && action.apiPath) {
      const firstRecord = records[0] ?? {};
      const apiPath = fillLinkTemplate(action.apiPath, firstRecord);
      fetchJsonWithAuth(AUTH_URL, apiPath.startsWith('/') ? apiPath : `/${apiPath}`, {
        method: action.apiMethod || 'POST',
        body: ['GET', 'DELETE'].includes(String(action.apiMethod).toUpperCase())
          ? undefined
          : JSON.stringify({ ids, records }),
      })
        .then(() => {
          message.success(`${action.label} completed`);
          setRefreshKey((value) => value + 1);
        })
        .catch((error) => message.error(error.message || `${action.label} failed`));
      return;
    }
    if (action.route) {
      const firstRecord = records[0] ?? {};
      const resolvedRoute = fillLinkTemplate(action.route, firstRecord);
      const normalizedRoute = resolvedRoute.startsWith('/') ? resolvedRoute : `/${resolvedRoute}`;

      if (action.route.startsWith('/popup/') || action.route.startsWith('popup/')) {
        const popupKeyPath = action.popupKey || action.route?.replace(/^\/popup\//i, '') || '';
        const isQuickSubmitPopup = /(?:^|\/)(?:quick(?:-|_)?submit|submit)(?:$|\/)/i.test(popupKeyPath);
        const urlJobId = new URLSearchParams(location.search).get('jobId');
        const quickSubmitJobId = action.popupState?.jobId
          ?? action.popupState?.jobID
          ?? action.popupState?.jobId
          ?? firstRecord.jobId
          ?? firstRecord.job?.id
          ?? firstRecord.job?._id
          ?? urlJobId;
        const quickSubmitCandidateId = action.popupState?.candidateId
          ?? action.popupState?.applicantId
          ?? firstRecord.id
          ?? firstRecord._id
          ?? firstRecord.applicantId
          ?? firstRecord.candidateId;

        if (isQuickSubmitPopup && quickSubmitJobId && quickSubmitCandidateId) {
          const params = new URLSearchParams();
          params.set('candidateId', String(quickSubmitCandidateId));
          params.set('isFromSubmission', 'false');
          navigate(`/submission-create/${encodeURIComponent(String(quickSubmitJobId))}?${params.toString()}`);
          return;
        }

        navigate(normalizedRoute, {
          state: {
            background: location,
            selectedIds: ids,
            selectedRecords: records,
            moduleName: baseModuleName,
            popupContext: action.popupState ?? {},
            popupAction: {
              key: action.key,
              apiMethod: action.apiMethod,
              apiPath: action.apiPath,
              defaultTemplateId: action.defaultTemplateId,
              requireJobId: action.requireJobId,
              popupFieldOverrides: action.popupFieldOverrides,
            },
            ...action.popupState,
          },
        });
        return;
      }
      navigate(normalizedRoute);
    }
  }

  function handleActionClick({ key }) {
    const action = flattenActions(selectedActionItems).find((a) => String(a.key) === String(key));
    // A "done" toolbar action is a disabled success label, not a trigger.
    if (action && bulkDoneState(action, selectedRows, activeTab)) return;
    handleActionClickForRecords(key, selectedRowKeys, selectedRows);
  }
  function handleRowActionClick(record, index) {
    return ({ key }) => handleActionClickForRecords(key, [resolveRecordId(record, index)], [record]);
  }

  const allColumnsVisible = visibleKeys.length === tableFields.length;
  const someColumnsVisible = visibleKeys.length > 0 && !allColumnsVisible;

  const toggleColumn = (key, checked) => {
    setVisibleColumnKeys((current) => {
      const defaultKeys = tableFields.slice(0, defaultVisibleColumnCount).map((field) => field.key);
      const currentKeys = current.length ? current : defaultKeys;

      if (checked) return currentKeys.includes(key) ? currentKeys : [...currentKeys, key];
      if (currentKeys.length <= minimumVisibleColumns) return currentKeys;

      return currentKeys.filter((columnKey) => columnKey !== key);
    });
  };

  const handleSummaryClick = () => {
    if (typeof onSummaryClick === 'function') {
      onSummaryClick();
      return;
    }
    if (summaryRoute) {
      navigate(summaryRoute);
      return;
    }
    setSummaryOpen(true);
  };

  const handleFieldButtonClick = () => {
    if (fieldButtonRoute) {
      navigate(fieldButtonRoute);
    }
  };

  useEffect(() => {
    if (!summaryOpen) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setSummaryOpen(false);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [summaryOpen]);

  const restoreDefaultListSnapshot = () => {
    if (hasUsableDefaultSnapshot) {
      setModuleRows(defaultListSnapshot.rows);
      setModuleTotal(defaultListSnapshot.total);
      setModuleTabs(defaultListSnapshot.tabs);
      setModuleFields(defaultListSnapshot.fields);
      setModuleTabField(defaultListSnapshot.tabField);
      setModuleActions(defaultListSnapshot.actions ?? []);
      setModuleActionRules(defaultListSnapshot.actionRules ?? []);
      setModuleError(null);
      setModuleLoading(false);
    }
  };

  const handleSearchChange = (event) => {
    const nextSearch = event.target.value;
    const searchWillBeInactive = nextSearch.trim().length < 3;
    const hasAppliedFieldFilters = appliedFilterRows.some((row) => row.field);

    if (searchWillBeInactive) {
      setCommittedSearch('');
      if (!hasAppliedFieldFilters) {
        restoreDefaultListSnapshot();
      }
    }

    setSearch(nextSearch);
    resetPaginationPage();
  };

  const handleSearchEnter = () => {
    const q = search.trim();
    if (q.length >= 3) {
      setCommittedSearch(q);
      resetPaginationPage();
    }
  };

  const handleFilterReset = () => {
    restoreDefaultListSnapshot();
    setAppliedFilterRows([]);
    updateSelection([], []);
    resetPaginationPage();
  };

  // Clear card selection when the user navigates to a different page or tab
  // so the Actions dropdown never carries stale records from the previous page.
  useEffect(() => {
    if (resolvedActiveView === 'card') {
      setSelectedCardIds([]);
      updateSelection([], []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.current, pagination.pageSize, activeTab, resolvedActiveView]);

  // --------------------------------------------------------------------------
  // 4g. NEW — Card View Handlers
  //     handleCardSelect: toggles a card's checked state and mirrors it into
  //     selectedRowKeys / selectedRows so the shared Actions dropdown fires
  //     identically in both List and Card views.
  // --------------------------------------------------------------------------
  const handleCardSelect = (key) => {
    setSelectedCardIds((prev) => {
      const next = prev.includes(key)
        ? prev.filter((id) => id !== key)
        : [...prev, key];
      const nextRows = pagedFilteredData.filter((record, idx) =>
        next.includes(String(getRowKey(record, idx, rowKey)))
      );
      updateSelection(next, nextRows);
      return next;
    });
  };

  const columnVisibilityContent = (
    <div className="column-visibility-menu" onClick={(event) => event.stopPropagation()}>
      <Checkbox
        checked={allColumnsVisible}
        indeterminate={someColumnsVisible}
        onChange={(event) => {
          setVisibleColumnKeys(
            event.target.checked
              ? tableFields.map((field) => field.key)
              : tableFields.slice(0, defaultVisibleColumnCount).map((field) => field.key)
          );
        }}
      >
        Select All
      </Checkbox>
      <div className="column-visibility-menu__list">
        {tableFields.map((field) => (
          <Checkbox
            key={field.key}
            checked={visibleKeys.includes(field.key)}
            disabled={visibleKeys.includes(field.key) && visibleKeys.length <= minimumVisibleColumns}
            onChange={(event) => toggleColumn(field.key, event.target.checked)}
          >
            {field.label}
          </Checkbox>
        ))}
      </div>
    </div>
  );
  return (
    <div
      className={`antd dynamic-list-view${compact ? ' list-view--compact' : ''}${shouldLockOuterPageScroll ? ' list-view--page-scroll-lock' : ''} ${className}`.trim()}
    >
      {breadcrumbItems.length > 0 && (
        <div className="job-list-header">
          <AppBreadcrumb items={breadcrumbItems} />
        </div>
      )}
      <Card className="job-tabs-card">
        <Flex align="center" justify="space-between">
          <DynamicTabs
            activeKey={activeTab}
            tabs={listTabs}
            onChange={handleTabChangeInternal}
          />
          <Flex align="center" gap={8}>
            {toolbarExtra}
            {shouldShowSearchInput && (
              <Input
                prefix={<SearchOutlined className="job-search-icon" />}
                placeholder={searchPlaceholder}
                value={search}
                onChange={handleSearchChange}
                onPressEnter={handleSearchEnter}
                allowClear
                className="job-search-input"
              />
            )}
            {!isControlled && canAdd && showAdd && (
              <Tooltip title="Add">
                <Button
                  aria-label="Add"
                  className="job-toolbar-icon-button"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    if (createRoute) navigate(createRoute);
                  }}
                />
              </Tooltip>
            )}
            {hasSourceRows && (<Tooltip title="Filter">
              <Badge
                count={appliedFilterRows.filter((r) => r.field && hasFilterValue(r)).length}
                size="small"
                offset={[-2, 2]}
              >
                <Button
                  aria-label="Filter"
                  className="job-toolbar-icon-button"
                  icon={<FilterOutlined />}
                  onClick={() => setFiltersOpen(true)}
                />
              </Badge>
            </Tooltip>)}

            {showViewToggle && hasSourceRows && (
              <Flex align="center" gap={6}>
                <Tooltip title="List View">
                  <Button
                    className={`job-toolbar-icon-button param-view-toggle-btn${resolvedActiveView === 'list' ? ' param-view-toggle-btn--active' : ''}`}
                    icon={<UnorderedListOutlined />}
                    onClick={() => { onViewChange?.('list'); setSelectedCardIds([]); updateSelection([], []); }}
                  />
                </Tooltip>
                <Tooltip title="Card View">
                  <Button
                    className={`job-toolbar-icon-button param-view-toggle-btn${resolvedActiveView === 'card' ? ' param-view-toggle-btn--active' : ''}`}
                    icon={<AppstoreOutlined />}
                    onClick={() => { onViewChange?.('card'); setSelectedCardIds([]); updateSelection([], []); }}
                  />
                </Tooltip>
              </Flex>
            )}

            {showSummaryButton && (
              <Button
                className="job-summary-button"
                type="primary"
                onClick={handleSummaryClick}
              >
                {summaryButtonLabel}
              </Button>
            )}
            {showFieldButton && fieldButtonRoute && (
              <Button
                className="job-summary-button"
                type="primary"
                onClick={handleFieldButtonClick}
              >
                {fieldButtonLabel}
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>
      {hasSourceRows && (
        <>
          <Card className="job-toolbar-card">
            <Flex className="p-flex" align="center" justify="space-between">
              <Flex align="center" gap={3}>
                {resolvedActiveView === 'card' && (
                  <Checkbox
                    checked={pagedFilteredData.length > 0 && selectedCardIds.length >= pagedFilteredData.length}
                    indeterminate={selectedCardIds.length > 0 && selectedCardIds.length < pagedFilteredData.length}
                    className="param-card-view__select-all"
                    onChange={() => {
                      if (selectedCardIds.length > 0) {
                        setSelectedCardIds([]);
                        updateSelection([], []);
                      } else {
                        const allKeys = pagedFilteredData.map((record, idx) => String(getRowKey(record, idx, rowKey)));
                        setSelectedCardIds(allKeys);
                        updateSelection(allKeys, [...pagedFilteredData]);
                      }
                    }}
                  >
                    Select All
                  </Checkbox>
                )}
                <Dropdown
                  open={columnMenuOpen}
                  onOpenChange={setColumnMenuOpen}
                  trigger={['click']}
                  dropdownRender={() => columnVisibilityContent}
                  placement="bottomLeft"
                  overlayClassName="column-visibility-dropdown"
                >
                  <Button
                    aria-label="Display columns"
                    type="text"
                    icon={<img src={unorderedListOutlinedIcon} alt="" className="job-action-list-icon" />}
                    size="small"
                    className="job-actions-button job-column-display-button"
                  >
                    <DownOutlined className="job-actions-caret" />
                  </Button>
                </Dropdown>
                {selectedActionItems.length > 0 && selectedRowKeys.length > 0 && (
                  <Dropdown
                    menu={{
                      items: selectedActionItems.map((action) => toMenuItem(action, bulkDoneState(action, selectedRows, activeTab))),
                      onClick: handleActionClick,
                    }}
                    trigger={['click']}
                  >
                    <Button
                      type="text"
                      size="small"
                      className="job-actions-button job-actions-menu-button"
                    >
                      <img src={frameIcon} alt="" className="job-actions-frame-icon" />
                      Actions <DownOutlined className="job-actions-caret" />
                    </Button>
                  </Dropdown>
                )}
                {selectedRowKeys.length > 0 && (
                  <Text className="job-selected-count">
                    Selected ({selectedRowKeys.length})
                  </Text>
                )}
              </Flex>
              {showSearchResultsCount && (
                <Text className="job-search-results-count">
                  Search Results ({searchResultsCount})
                </Text>
              )}
            </Flex>
          </Card>
        </>)}

      {hasActiveFieldFilters && (
        <FilterChipsBar
          filters={appliedFilterRows}
          onRemove={(originalIdx) => {
            const next = appliedFilterRows.filter((_, i) => i !== originalIdx);
            setAppliedFilterRows(next);
            resetPaginationPage();
          }}
          onClearAll={handleFilterReset}
        />
      )}

      <div ref={tableContainerRef} className="job-table-measure">
        <Spin spinning={isControlled ? propLoading : (configLoading || moduleLoading)}>
          {resolvedActiveView === 'card' ? (
            pagedFilteredData.length === 0 ? (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={emptyMessage}
                className="param-card-view__empty"
              >
                {!isControlled && canAdd && showAdd && createRoute && !moduleError && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => navigate(createRoute)}
                  >
                    Add New
                  </Button>
                )}
              </Empty>
            ) : (
              <div className="param-card-view__grid">
                {pagedFilteredData.map((record, idx) => {
                  const key = String(getRowKey(record, idx, rowKey));
                  return (
                    <RecordCard
                      key={key}
                      record={record}
                      fields={visibleCardFields}
                      selected={selectionMode === 'checkbox' ? selectedCardIds.includes(key) : isRecordSelected(record, idx)}
                      isControlled={isControlled}
                      onPreview={() => handlePreviewOpen(record)}
                      onSelect={() => handleCardSelect(key)}
                      onPopupAction={openRowPopup}
                      onRecordClick={onRecordClick ? () => handleRecordClick(record, idx) : undefined}
                      selectionMode={selectionMode}
                      permissions={{ canAdd, canEdit, canDelete }}
                    />
                  );
                })}
              </div>
            )
          ) : (
            // <Table
            //   rowSelection={hasSourceRows ? {
            //     type: 'checkbox',
            //     selectedRowKeys,
            //     onChange: (keys, rows) => {
            //       setSelectedRowKeys(keys);
            //       setSelectedRows(rows);  
            //     },
            //     columnTitle: (() => {
            //       const totalOnPage = pagedFilteredData.length;
            //       const allSelected = totalOnPage > 0 && selectedRowKeys.length >= totalOnPage;
            //       const someSelected = selectedRowKeys.length > 0 && !allSelected;
            //       return (
            //         <Checkbox
            //           checked={allSelected}
            //           indeterminate={someSelected}
            //           onChange={() => {
            //             if (someSelected || allSelected) {
            //               setSelectedRowKeys([]);
            //               setSelectedRows([]);
            //             } else {
            //               const allKeys = pagedFilteredData.map((record, idx) => getRowKey(record, idx, rowKey));
            //               setSelectedRowKeys(allKeys);
            //               setSelectedRows([...pagedFilteredData]);
            //             }
            //           }}
            //         />
            //       );
            //     })(),
            //   } : undefined}
            //   columns={columns}
            //   dataSource={pagedFilteredData}
            //   size="middle"
            //   showSorterTooltip={false}
            //   tableLayout="fixed"
            //   scroll={(hasHorizontalScroll || isControlled) ? { x: 'max-content' } : undefined}
            //   pagination={false}
            //   onChange={handleTableChange}
            //   locale={{
            //     emptyText: (
            //       <Empty
            //         image={Empty.PRESENTED_IMAGE_SIMPLE}
            //         description={emptyMessage}
            //       />
            //     ),
            //   }}
            //   className={tableClassName}
            //   rowKey={(record, index) => getRowKey(record, index, rowKey)}
            // />
            <Table
              rowSelection={hasSourceRows && selectionMode === 'checkbox' ? {
                type: 'checkbox',
                fixed: true,
                columnWidth: 48,
                selectedRowKeys,
                onChange: (keys, rows) => {
                  updateSelection(keys, rows);
                },
                columnTitle: (() => {
                  const totalOnPage = pagedFilteredData.length;
                  const allSelected = totalOnPage > 0 && selectedRowKeys.length >= totalOnPage;
                  const someSelected = selectedRowKeys.length > 0 && !allSelected;
                  return (
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                      onChange={() => {
                        if (someSelected || allSelected) {
                          updateSelection([], []);
                        } else {
                          const allKeys = pagedFilteredData.map((record, idx) => getRowKey(record, idx, rowKey));
                          updateSelection(allKeys, [...pagedFilteredData]);
                        }
                      }}
                    />
                  );
                })(),
              } : undefined}
              columns={columns}
              dataSource={pagedFilteredData}
              size="middle"
              tableLayout="fixed"
              scroll={{ x: (hasHorizontalScroll || isControlled) ? 'max-content' : undefined, y: 'calc(100vh - 300px)' }}
              pagination={false}
              locale={{
                emptyText: (
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={emptyMessage}
                  />
                ),
              }}
              className={`${tableClassName} ${compact ? 'list-view-table--compact' : ''}`.trim()}
              onRow={onRecordClick ? (record, index) => ({
                onClick: (event) => {
                  if (isInteractiveClickTarget(event.target)) return;
                  handleRecordClick(record, index);
                },
                'aria-selected': isRecordSelected(record, index),
              }) : undefined}
              rowClassName={selectedRecordId != null ? (record, index) => (
                isRecordSelected(record, index) ? 'list-view-row--active' : ''
              ) : undefined}
              rowKey={(record, index) => getRowKey(record, index, rowKey)}
            />
          )}
        </Spin>
      </div>

      {hasSourceRows ? <CustomPagination
        current={pagination.current}
        pageSize={pagination.pageSize}
        total={tableTotal}
        onChange={(page, nextPageSize) => {
          if (isControlled && typeof onPageChange === 'function') {
            onPageChange(page, nextPageSize ?? pagination.pageSize);
            return;
          }
          setPaginationPage(page);
        }}
        onPageSizeChange={(size) => {
          if (isControlled && typeof onPageSizeChange === 'function') {
            onPageSizeChange(size);
            return;
          }
          setPagination({ current: 1, pageSize: size });
          updatePageRoute(1, { replace: true });
        }}
      /> : undefined}

      <DynamicFieldFilter
        moduleName={baseModuleName}
        open={filtersOpen}
        filters={appliedFilterRows}
        onClose={() => setFiltersOpen(false)}
        onApply={({ filters }) => {
          setAppliedFilterRows(filters);
          resetPaginationPage();
        }}
        onReset={handleFilterReset}
      />
      <ViewSummaryWidget
        moduleName={baseModuleName}
        open={summaryOpen}
        rows={moduleRows}
        tabs={listTabs}
        total={tableTotal}
        onClose={() => setSummaryOpen(false)}
      />
    </div>
  );
}
